import { create } from 'zustand';
import {
  initializeWebexJitsiMeetingService,
  resetWebexJitsiMeetingService,
  webexJitsiService,
} from '@shared/singletons/jitsi-singleton';
import { ApiService, UserType } from '@shared/services/api.service';
import { JitsiConferenceEvents } from 'nexgen-types/lib-jitsi';
import { nanoid } from 'nanoid';
import { EventTypes } from 'nexgen-types';
import { GlobalEventService } from '@/host/services/event.service';
import { useWorkplaceStore } from '@/host/stores/workplace.store';
import { useNotificationStore } from '@/host/stores/notification.store';
import { useLiveCaptionsStore, TranscriptEntry } from '@/host/stores/liveCaptions.store';
import { useMultiFloorStateStore } from '@/apps/office/stores/MultiFloorStore';
import { translationService } from '@/shared/services/translation.service';

declare global {
  interface Window {
    Webex: any;
    JitsiMeetJS: any;
  }
}

const API_ROOT = import.meta.env?.VITE_APP_API_ROOT || 'http://localhost:5500';

// Allowed origins for postMessage events
const ALLOWED_ORIGINS = [window.location.origin, API_ROOT].filter(Boolean);

const logger = {
  debug: (message: string, data?: any) => {
    if (import.meta.env?.DEV) {
      console.log(`🔍 [WebEx] ${message}`, data || '');
    }
  },
  info: (message: string, data?: any) => {
    console.info(`ℹ️ [WebEx] ${message}`, data || '');
  },
  warn: (message: string, data?: any) => {
    console.warn(`⚠️ [WebEx] ${message}`, data || '');
  },
  error: (message: string, error?: any) => {
    console.error(`❌ [WebEx] ${message}`, error || '');
    if (!import.meta.env?.DEV && error) {
      console.error('Production error tracked:', { message, error });
    }
  },
};

// Camera / mic track registry
const activeCameraTracks = new Set<MediaStreamTrack>();
const activeMicrophoneTracks = new Set<MediaStreamTrack>();

// All mic tracks we've owned — lets the sweep below stop only our own mic senders.
// Cleared in cleanup().
const ownedMicrophoneTrackIds = new Set<string>();

// While true, capture SDK-acquired mic tracks; off during cleanup so Jitsi's mic is never stopped.
let _webexMicTrackingActive = false;

// Superseded initializeWebex() runs abort at each await (rapid room switching).
let _webexInitGeneration = 0;
// Bumped on every screen-share teardown — a connect landing after one is left, not adopted.
let _jitsiScreenShareGeneration = 0;
// Room the newest run targets — detects joins that resolve after moving rooms.
let _webexInitTargetRoom: string | null = null;
/** Longest a click-time overlay may wait for initializeWebex to take it over. */
const JOIN_FEEDBACK_FAILSAFE_MS = 10_000;
let _joinFeedbackSeq = 0;
// The meeting link is a call's true identity — compare links, not room ids, to decide teardown.
let _webexInitTargetLink: string | null = null;

// --- Meeting reboot (stop-recording restart cycle) ---
// Ending the meeting for everyone terminates the locus, which is what makes Webex finalize
// the recording; the rejoin then starts a fresh instance on the same link.
//
// Delay between ending the meeting and green-lighting the rejoin. Too short and Webex may
// treat the rejoin as the same instance, so the recording never finalizes and its webhook
// never fires — a silent failure. 2000ms is not arbitrary: a test run where the SDK
// rejoined ~1.3s after the locus ended already produced a fresh conference (new
// webexConferenceIdStr), so the teardown grace is comfortably under two seconds.
const REBOOT_REJOIN_DELAY_MS = 2000;

// SDK recording states meaning "recording on" (idle is the only off state). Lowercase —
// the SDK's RECORDING_STATE enum is {RECORDING:"recording", IDLE:"idle", PAUSED:"paused",
// RESUMED:"resumed"}, and comparing against uppercase silently never matched.
const RECORDING_STATES = ['recording', 'paused', 'resumed'];

// An ejection we did not initiate is optimistically treated as a reboot: the overlay stays
// up until a restart event confirms it, or this window lapses and it settles into hung-up.
// Covers the common case without needing a "reboot starting" broadcast to win a race
// against Webex's own locus signalling. 12s: the restart event arrives ~2-3s after the end
// (2s gap + transport), so this leaves generous margin while keeping the worst case — a
// genuine end mistaken for a reboot — from blocking the hung-up UI for very long.
const REBOOT_WAIT_MS = 12_000;
let _rebootWaitTimer: ReturnType<typeof setTimeout> | null = null;
// In-flight settle cleanup after a reboot wait expired; a new join awaits it (see initializeWebex).
let _settleCleanup: Promise<void> | null = null;
// Per-room restart-cycle bookkeeping: one emit arrives three ways (postMessage,
// BroadcastChannel, socket echo), and a delayed delivery of an OLDER cycle must not force
// another rejoin of a healthy session. The seen-set dedupes repeats; lastTs (the
// initiator's own clock, from the payload timestamp) rejects late cycles a set can't —
// a never-seen cycle A arriving after cycle B is still A-old.
const _restartCycleState = new Map<string, { seen: Set<string>; lastTs: number }>();
const MAX_CYCLE_STATE_ROOMS = 20;
const MAX_SEEN_CYCLE_IDS = 50;

const shouldProcessRestartCycle = (
  roomId: string,
  cycleId: string | undefined,
  timestamp: string | undefined
): boolean => {
  if (!cycleId) return true; // legacy emits without a cycle id — nothing to dedupe on
  const entry = _restartCycleState.get(roomId) ?? { seen: new Set<string>(), lastTs: 0 };
  if (entry.seen.has(cycleId)) return false;
  const ts = timestamp ? new Date(timestamp).getTime() : 0;
  if (Number.isFinite(ts) && ts > 0 && entry.lastTs > 0 && ts < entry.lastTs) return false;
  entry.seen.add(cycleId);
  if (entry.seen.size > MAX_SEEN_CYCLE_IDS) entry.seen.clear();
  if (Number.isFinite(ts) && ts > entry.lastTs) entry.lastTs = ts;
  if (!_restartCycleState.has(roomId)) {
    if (_restartCycleState.size >= MAX_CYCLE_STATE_ROOMS) {
      _restartCycleState.delete(_restartCycleState.keys().next().value as string);
    }
    _restartCycleState.set(roomId, entry);
  }
  return true;
};

// IN_LOBBY member deltas repeat as the SDK re-reports the same state — one admit call per
// member per window is enough; the rest only feed the backend's "Admit by IDs failed" noise.
// Remote media objects are long-lived per pane, and layoutChanged fires repeatedly (main
// pane, then thumbnails, and again on every renegotiation). Attaching a fresh closure each
// time stacked duplicate sourceUpdate listeners, so one event ran the handler N times and
// pushed N video entries for the same participant — the duplicate-tile bug. WeakSet so
// replaced media objects are still collectable.
const _sourceUpdateAttached = new WeakSet<object>();

// Starting/stopping a screen share makes the SDK re-lay-out the remote panes: camera streams
// get paused, swapped or ended even though nobody left. Inferred-disconnect heuristics (ended
// tracks, frozen frames) can't tell that from a real drop, so they stand down while a share
// is active or the layout has just changed; members:update stays the authority on who left.
const SHARE_LAYOUT_SETTLE_MS = 8000;
let lastShareLayoutChangeAt = 0;
const markShareLayoutChange = () => {
  lastShareLayoutChangeAt = Date.now();
};
export const isWebexShareTransition = (): boolean => {
  const { isScreenSharing, screenShareVideo, remoteScreenShares } = useWebexStore.getState();
  return (
    !!isScreenSharing ||
    !!screenShareVideo ||
    // Only shares that are still live — a stale entry from a finished share must not keep
    // the disconnect heuristics switched off.
    (remoteScreenShares ?? []).some((share: any) =>
      share?.stream?.getVideoTracks?.().some((t: MediaStreamTrack) => t.readyState === 'live')
    ) ||
    Date.now() - lastShareLayoutChangeAt < SHARE_LAYOUT_SETTLE_MS
  );
};
// Stable slot key for media objects without id/csi — pane indices are not stable across
// layout changes, so a per-object synthetic key keeps events matched to the right tile.
const _slotKeysByMedia = new WeakMap<object, string>();
let _slotKeyCounter = 0;

const slotKeyForMedia = (remoteMedia: any): string => {
  let key = remoteMedia?.id ?? remoteMedia?.csi;
  if (!key) {
    key = _slotKeysByMedia.get(remoteMedia);
    if (!key) {
      key = `slot-${++_slotKeyCounter}`;
      _slotKeysByMedia.set(remoteMedia, key);
    }
  }
  return key;
};

const _lastLobbyAdmitAt = new Map<string, number>();
const LOBBY_ADMIT_THROTTLE_MS = 5_000;

const clearRebootWait = (): void => {
  if (_rebootWaitTimer) {
    clearTimeout(_rebootWaitTimer);
    _rebootWaitTimer = null;
  }
};

// Meetings plugins whose `meeting:removed` event is already wired to the ejection handling.
const _ejectionHookAttached = new WeakSet<object>();

/**
 * The locus dropped us without us having issued a leave (an endMeetingForAll cycle, a host
 * removal, or a server-side disconnect for network inactivity).
 *
 * `reason` discriminates the two recoveries:
 * - Network inactivity → rejoin at once. That is what the SDK's autoRejoin used to do for
 *   us (we run with reconnection.autoRejoin:false so the reboot flow can own the rejoin),
 *   and waiting for a restart event would never pay off — none is coming.
 * - Meeting termination → hold the overlay optimistically until a restart event arrives,
 *   or settle into the normal hung-up UI after REBOOT_WAIT_MS.
 */
const handleEjectedFromMeeting = (meeting: any, reason: string, wasEjected: boolean): void => {
  logger.info('[reboot] Ejected from meeting', {
    reason: reason || 'unknown',
    meetingId: meeting?.id,
  });

  // Stop captions silently — remaining participants keep theirs.
  useWebexStore
    .getState()
    .stopLiveCaptions(false)
    .catch((error: any) => logger.warn('[reboot] Failed to stop captions', error));
  unsubscribeWebexLiveCaptions();
  useWebexStore.setState({
    isInMeeting: false,
    // Left true across a reboot on purpose: CoreWebexAuth treats isMeetingHungUp:false +
    // isStartingMeeting:false as "auto-connect", which would rejoin before the locus has
    // torn down. The restart event drives the rejoin instead.
    isMeetingHungUp: true,
    isReconnecting: false,
    meeting: null,
    participants: [],
    isStartingMeeting: false,
  });

  // Everything below is for ejections only. On a leave we issued, the caller
  // (leaveMeeting / cleanup) already owns teardown, and running a second cleanup
  // concurrently would clear joinStage out from under an in-flight join.
  if (!wasEjected) return;

  const isNetworkDrop = reason === 'CALL_INACTIVE' || reason === 'INACTIVE';
  if (isNetworkDrop) {
    logger.warn('[reboot] Ejected for network inactivity — rejoining immediately');
    // Cancel any earlier optimistic-wait timer — this rejoin IS the resolution, and a
    // leftover timer firing mid-join would blank joinStage and drop the overlay early.
    clearRebootWait();
    // reInitializeWebex leaves any SDK-held meeting and drains tracks as its first steps,
    // so no separate cleanup is needed here. Its bounded retries absorb a still-flaky
    // network; if they are exhausted the failure path lands on the hung-up UI.
    useWebexStore
      .getState()
      .reInitializeWebex()
      .catch((error: any) => {
        logger.error('[reboot] Rejoin after network drop failed', error);
        useWebexStore.setState({ isRestarting: false, joinStage: '', restartReason: null });
      });
    return;
  }

  // Assume a reboot is underway and hold the overlay. If no restart arrives, this was a
  // genuine end-of-meeting and it settles into the normal hung-up UI.
  //
  // The only automated meeting-end in this product is the stop-recording reboot, so the
  // recording popup is shown for ALL meeting-termination ejections from this point on —
  // participants see it through the whole wait, not just after the restart event lands.
  // Trade-off: a genuinely host-ended meeting shows the same popup until the settle
  // (~REBOOT_WAIT_MS) flips to the hung-up UI.
  logger.info('[reboot] Awaiting restart before settling');
  clearRebootWait();
  useWebexStore.setState({
    isRestarting: true,
    restartReason: 'reboot',
    isRestartInitiator: false,
  });
  _rebootWaitTimer = setTimeout(() => {
    _rebootWaitTimer = null;
    if (!useWebexStore.getState().isRestarting) return;
    logger.info('[reboot] No restart within the reboot window — treating as a real end');
    useWebexStore.setState({ isRestarting: false, joinStage: '', restartReason: null });

    // No restart is coming — now release mic/camera. Deliberately NOT done at ejection
    // time: a fire-and-forget cleanup could land after the rejoin had repopulated the
    // store (the same race that wiped the ender's tiles — its resetState nulls
    // localVideo/participants/remoteVideos). When a restart DOES arrive, the rejoin's own
    // awaited cleanup inside initializeWebex handles the teardown before the join.
    // Cost: on a genuine end the OS capture indicator stays lit until this settle.
    // Tracked in _settleCleanup so a Reconnect clicked mid-cleanup awaits it first.
    _settleCleanup = useWebexStore
      .getState()
      .cleanup(false, true)
      .catch((error: any) => logger.warn('[reboot] Cleanup after settle failed', error))
      .finally(() => {
        _settleCleanup = null;
      });
  }, REBOOT_WAIT_MS);
};

/**
 * A join failure that reads as "the room's meeting no longer exists on Webex".
 *
 * The backend decides whether to reuse a meeting from its own expires_at column, so a
 * meeting ended before its 40-minute window lapsed — by our own reboot cycle, or by a host
 * ending it from a native Webex client — still reads as viable and keeps being handed out.
 * Every join against that dead link then fails until the window runs out. Recognising the
 * failure is what lets initializeWebex retire the row and mint a fresh meeting instead.
 *
 * NOTE: the exact shape Webex returns here is unconfirmed against a real ended meeting, so
 * this matches broadly and the recovery it gates is capped at one attempt per join.
 */
const isMeetingGoneError = (error: any): boolean => {
  const status = error?.httpStatusCode ?? error?.statusCode ?? error?.response?.status;
  if (status === 404 || status === 410) return true;

  const text = [error?.message, error?.sdkMessage, JSON.stringify(error?.body ?? {})]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return (
    text.includes('meeting not found') ||
    text.includes('does not exist') ||
    text.includes('has ended') ||
    text.includes('meeting ended') ||
    text.includes('no longer available')
  );
};

/** 423 / errorCode 2423012 — the meeting owner's concurrent active meeting limit. */
const isConcurrencyLimitError = (error: any): boolean => {
  const text = `${error?.httpStatusCode ?? error?.statusCode ?? ''} ${error?.message ?? ''} ${
    error?.sdkMessage ?? ''
  } ${JSON.stringify(error?.body ?? {})}`.toLowerCase();
  return (
    error?.httpStatusCode === 423 ||
    error?.statusCode === 423 ||
    text.includes('2423012') ||
    text.includes('concurrent')
  );
};

/** Flip the room's meeting row to expired so the next create mints a fresh meeting. */
const retireRoomMeeting = async (roomId: string): Promise<boolean> => {
  try {
    const result = await ApiService.post<{ retired: boolean; webexId: string | null }>(
      `apps/webex/meeting/retire?roomId=${encodeURIComponent(roomId)}`
    );
    logger.info('[join] Retired the room meeting after a failed join', result);
    return !!result?.retired;
  } catch (error) {
    logger.warn('[join] Failed to retire the room meeting', error);
    return false;
  }
};

// Leave against an already-left participant returns 409 — expected during teardown.
const isAlreadyLeftError = (error: any): boolean => {
  if (error?.statusCode === 409 || error?.statusCode === 404) return true;
  const message = `${error?.message ?? ''} ${error?.body?.message ?? ''}`.toLowerCase();
  return (
    message.includes('invalid for current operation') ||
    message.includes('state: left') ||
    message.includes('not found')
  );
};

// The SDK recycles Meeting objects per link — guard against double leave() (409).
const LEAVE_ISSUED = '__nexgenLeaveIssued';

const leaveMeetingOnce = async (meeting: any): Promise<void> => {
  if (!meeting) return;
  if (meeting[LEAVE_ISSUED]) {
    logger.debug('Leave already issued for this meeting, skipping duplicate leave');
    // Still drop it from the SDK's collection: a skipped leave (ended locus) left the
    // object behind, and the next createMeeting would find it via getMeetingByType and
    // recycle a dead meeting — a rejoin would then render zero tiles (no events, no
    // media) while the SDK runs the real session under a different internal object.
    meeting.webex?.meetings?.meetingCollection?.delete?.(meeting.id);
    return;
  }
  meeting[LEAVE_ISSUED] = true;
  try {
    await meeting.leave();
    // The SDK drops the meeting from its collection only once the locus leave event
    // is processed; drop it now so a lost event can't leave a stale locus behind.
    meeting.webex?.meetings?.meetingCollection?.delete?.(meeting.id);
  } catch (error) {
    // Reset the flag on failure too, or a stuck meeting could never be left again.
    meeting[LEAVE_ISSUED] = false;
    throw error;
  }
};

const markMeetingJoined = (meeting: any): void => {
  if (meeting) meeting[LEAVE_ISSUED] = false;
};

// Patch getUserMedia so every SDK-acquired audio track lands in activeMicrophoneTracks.
if (
  typeof navigator !== 'undefined' &&
  navigator.mediaDevices &&
  !(navigator.mediaDevices as any).__audioTracksPatchApplied
) {
  (navigator.mediaDevices as any).__audioTracksPatchApplied = true;
  const _origGUM = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
  navigator.mediaDevices.getUserMedia = async function (
    constraints: MediaStreamConstraints
  ): Promise<MediaStream> {
    const stream = await _origGUM(constraints);
    if (constraints?.audio && _webexMicTrackingActive) {
      stream.getAudioTracks().forEach((t) => {
        activeMicrophoneTracks.add(t);
        ownedMicrophoneTrackIds.add(t.id);
      });
    }
    return stream;
  };
}

function registerTracks(stream: any, bucket: Set<MediaStreamTrack>): void {
  if (!stream) return;
  const isMicBucket = bucket === activeMicrophoneTracks;
  const addFrom = (ms: any) =>
    ms?.getTracks?.().forEach((t: MediaStreamTrack) => {
      bucket.add(t);
      // Tracks acquired before the GUM patch (device check) are still ours.
      if (isMicBucket) ownedMicrophoneTrackIds.add(t.id);
    });
  addFrom((stream as any).inputStream);
  addFrom((stream as any).outputStream ?? stream);
}

function drainAndStop(bucket: Set<MediaStreamTrack>): void {
  bucket.forEach((t) => {
    try {
      t.stop();
    } catch (_) {
      /* ignore */
    }
  });
  bucket.clear();
}

function stopAllMicTracksFromStream(stream: any): void {
  if (!stream) return;

  const stopped = new Set<string>();

  const stopTrack = (t: MediaStreamTrack) => {
    if (t && !stopped.has(t.id)) {
      stopped.add(t.id);
      try {
        t.stop();
      } catch (_) {
        /* ignore */
      }
      activeMicrophoneTracks.delete(t);
    }
  };

  const stopFromMediaStream = (ms: any) => {
    if (!ms) return;
    if (typeof ms.getTracks === 'function') ms.getTracks().forEach(stopTrack);
  };

  stopFromMediaStream(stream.inputStream);
  stopFromMediaStream(stream.outputStream);
  stopFromMediaStream(stream.mediaStream);
  stopFromMediaStream(stream.stream);
  stopFromMediaStream(stream._stream);
  if (typeof stream.getMediaStream === 'function') stopFromMediaStream(stream.getMediaStream());
  if (typeof stream.getTracks === 'function') stream.getTracks().forEach(stopTrack);
  stopFromMediaStream(stream);
}

function stopAllCameraTracksFromStream(stream: any): void {
  if (!stream) return;

  const stopped = new Set<string>();

  const stopTrack = (t: MediaStreamTrack) => {
    if (t && !stopped.has(t.id)) {
      stopped.add(t.id);
      try {
        t.stop();
      } catch (_) {
        /* ignore */
      }
      activeCameraTracks.delete(t);
    }
  };

  const stopFromMediaStream = (ms: any) => {
    if (!ms) return;
    if (typeof ms.getTracks === 'function') {
      ms.getTracks().forEach(stopTrack);
    }
  };

  stopFromMediaStream(stream.outputStream);
  stopFromMediaStream(stream.mediaStream);
  stopFromMediaStream(stream.stream);
  stopFromMediaStream(stream._stream);
  if (typeof stream.getMediaStream === 'function') {
    stopFromMediaStream(stream.getMediaStream());
  }
  if (typeof stream.getTracks === 'function') {
    stream.getTracks().forEach(stopTrack);
  }
  stopFromMediaStream(stream);
}

const useWebexStore = create<WebexStore>((set, get) => ({
  webex: null,
  guestToken: null,
  isGuestSession: false,
  guestJoinUnavailable: false,
  guestJoinUnavailableReason: null,
  // True while the guest's placement room has no live meeting — the join is retried slowly
  // while this holds, so the guest joins the moment the host starts the meeting.
  guestNoMeeting: false,
  room: '',
  token: '',
  isStartingMeeting: false,
  joinStage: '',
  isJoinPending: false,
  meeting: null,
  meetingInfo: null,
  isConnected: false,
  isInMeeting: false,
  isWebexRecording: false,
  webexHostReclaimed: false,
  // Participant id that last started/stopped the recording, per the SDK's locus controls —
  // null when unknown (e.g. started before this client joined).
  webexRecordingModifiedBy: null,
  participants: [],
  chatMessages: [],
  localVideo: null,
  remoteVideo: null,
  isVideoEnabled: true,
  isAudioEnabled: true,
  audioStream: null,
  screenShareVideo: null,
  videoStream: null,
  localScreenShareVideo: null,
  localScreenShareAudio: null,
  personalMeeting: null,
  isScreenSharing: false,
  remoteVideos: [],
  remoteAudios: [],
  remoteScreenShares: [],
  activeSpeakerId: null,
  roomId: null,

  jitsiService: null,
  jitsiSubscription: null,
  jitsiScreenShareCount: 0,
  jitsiCleanupInterval: null,
  isJitsiConnected: false,
  availableDevices: [],
  videoDeviceId: localStorage.getItem('preferredVideoDeviceId') || null,
  audioDeviceId: localStorage.getItem('preferredAudioDeviceId') || null,

  broadcastChannel: null,

  canMinimizeMeetingPopup: false,
  dialogPosition: { x: 16, y: 0 },
  isMeetingHungUp: false,
  isLiveCaptionsActive: false,
  webexFloorPid: '',
  // True between the SDK's reconnectionStarting and reconnectionSuccess/Failure events.
  isReconnecting: false,
  // True across a stop-recording reboot cycle: from the ejection until the rejoin lands
  // (or REBOOT_WAIT_MS lapses). Drives the blocking overlay. Deliberately NOT
  // isMeetingHungUp — that flag gates CoreWebexAuth's auto-connect, and clearing it here
  // would fire an immediate rejoin before the locus has torn down.
  isRestarting: false,
  // Why the meeting is restarting, when known — 'reboot' marks the stop-recording cycle so
  // the overlay can show the recording-reboot popup. Null while the reason is unknown
  // (participants during the optimistic wait) or no restart is in flight.
  restartReason: null,
  // True only on the client that initiated the reboot (the recording ender) — lets the
  // overlay show the full join-progress popup to the initiator and a narrower, message-
  // focused popup to everyone else. Set on every isRestarting=true path, so it never
  // needs clearing elsewhere.
  isRestartInitiator: false,

  setIsStartingMeeting: (isStarting: boolean) => set({ isStartingMeeting: isStarting }),
  setJoinStage: (stage) => set({ joinStage: stage }),

  showJoinFeedback: (roomPid) => {
    const { isStartingMeeting, isJoinPending, isInMeeting, room } = get();
    if (isStartingMeeting || isJoinPending || (isInMeeting && room === roomPid)) return;
    // Not `room`: initializeWebex uses it to decide whether to leave the previous meeting.
    set({ isJoinPending: true, joinStage: 'preparing' });
    // Failsafe if nothing starts the join.
    const seq = ++_joinFeedbackSeq;
    setTimeout(() => {
      if (seq === _joinFeedbackSeq && !get().isStartingMeeting) get().clearJoinFeedback();
    }, JOIN_FEEDBACK_FAILSAFE_MS);
  },

  clearJoinFeedback: () => {
    // initializeWebex clears isJoinPending when it takes over, so a set flag is always ours.
    if (!get().isJoinPending) return;
    set({ isJoinPending: false, joinStage: '' });
  },
  setRemoteVideos: (videos: { stream: MediaStream; participantId: string }[]) =>
    set({ remoteVideos: videos }),
  setRemoteAudios: (audios: { stream: MediaStream; participantId: string }[]) =>
    set({ remoteAudios: audios }),
  setRemoteScreenShares: (
    screenShares: { stream: MediaStream; participantId: string; isLocal?: boolean }[]
  ) => set({ remoteScreenShares: screenShares }),
  setActiveSpeakerId: (id: string | null) => set({ activeSpeakerId: id }),
  setRoomId: (id: string) => set({ roomId: id }),

  setMinimizeMeetingPopup: (status: boolean) => set({ canMinimizeMeetingPopup: status }),

  setDialogPosition: (x: number, y: number) => {
    set({ dialogPosition: { x, y } });
  },

  setMeetingHungUpStatus: async (isHungUp: boolean) => {
    if (isHungUp) {
      set({ isMeetingHungUp: isHungUp, canMinimizeMeetingPopup: false });
      // End Call keeps the user in the room they are in — no redirect to the home room.
      await get().leaveMeeting();
    } else {
      const { room, token, isGuestSession } = get();
      set({ isMeetingHungUp: isHungUp });
      if (room && token) {
        const meetingPin = import.meta.env?.VITE_WEBEX_MEETING_PIN || '123456';
        // Reachable from the guest toolbar's Start Call, so isGuest must survive the rejoin.
        await get().initializeWebex(token, room, meetingPin, isGuestSession, true);
      }
    }
  },

  findOrCreateRoom: async (title: string) => {
    const { webex } = get();
    if (!webex) {
      logger.error('Webex not initialized');
      return null;
    }
    try {
      const rooms = await webex.rooms.list({ max: 100 });
      const existingRoom = rooms.items.find((room: any) => room.title === title);
      let room;
      if (existingRoom) {
        room = existingRoom;
      } else {
        room = await webex.rooms.create({ title, roomId: title });
      }
      set({ roomId: room.id });
      const initialSpaceMessage = {
        id: Date.now().toString(),
        text: `Join the meeting chat here: https://web.webex.com/space/${room.id}`,
        sender: 'System',
        timestamp: new Date().toLocaleTimeString(),
      };
      set({ chatMessages: [initialSpaceMessage] });
      return room;
    } catch (error) {
      logger.error('Error finding or creating room:', error);
      return null;
    }
  },

  setWebex: (webex: any) => set({ webex }),
  setMeeting: (meeting: any) => set({ meeting }),
  setMeetingInfo: (info: any) => set({ meetingInfo: info }),
  setIsConnected: (connected: boolean) => set({ isConnected: connected }),
  setIsInMeeting: (inMeeting: boolean) => set({ isInMeeting: inMeeting }),
  setWebexRecording: (recording: boolean) => set({ isWebexRecording: recording }),
  setParticipants: (participants: any[]) => set({ participants }),
  removeRemoteParticipant: (participantId: string) => {
    const { participants, remoteVideos } = get();
    set({
      participants: participants.filter((p: any) => p.id !== participantId),
      remoteVideos: remoteVideos.filter((v: any) => v.participantId !== participantId),
    });
  },
  addChatMessage: (message: any) =>
    set((state: any) => ({ chatMessages: [...state.chatMessages, message] })),
  setLocalVideo: (stream: MediaStream | null) => set({ localVideo: stream }),
  setRemoteVideo: (stream: MediaStream | null) => set({ remoteVideo: stream }),
  setUserProfile: (profile: any) => set({ userProfile: profile }),
  setAvailableDevices: (devices: any[]) => set({ availableDevices: devices }),
  setVideoDeviceId: (deviceId: string | null) => set({ videoDeviceId: deviceId }),
  setAudioDeviceId: (deviceId: string | null) => set({ audioDeviceId: deviceId }),

  // Drain the mic bucket so the OS microphone indicator turns off.
  stopMediaTrack: async (type: string) => {
    const { meeting, videoStream } = get();
    if (!meeting) {
      logger.warn('No meeting found to stop media track');
      return;
    }
    try {
      switch (type) {
        case 'audio':
          if (meeting.mediaProperties.audioStream) {
            stopAllMicTracksFromStream(meeting.mediaProperties.audioStream);
            await meeting.unpublishStreams(['microphone']);
          }
          drainAndStop(activeMicrophoneTracks);
          break;
        case 'video':
          if (meeting.mediaProperties.videoStream) {
            meeting.mediaProperties.videoStream.stop();
            videoStream?.stop();
            await meeting.unpublishStreams(['camera']);
          }
          drainAndStop(activeCameraTracks);
          break;
        case 'share':
          if (meeting.mediaProperties.shareVideoStream) {
            meeting.mediaProperties.shareVideoStream.stop();
            meeting.mediaProperties.shareAudioStream?.stop();
            await meeting.unpublishStreams(['screenShare']);
          }
          break;
      }
    } catch (error) {
      logger.error(`Error stopping ${type} track:`, error);
    }
  },

  switchCamera: async (deviceId: string) => {
    const { meeting, setVideoDeviceId } = get();
    if (!meeting) {
      logger.warn('No meeting found to switch camera');
      return;
    }
    try {
      setVideoDeviceId(deviceId);
      localStorage.setItem('preferredVideoDeviceId', deviceId);

      const videoConstraints = {
        deviceId: { exact: deviceId },
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 },
      };

      const { webex, videoStream } = get();

      if (videoStream) {
        stopAllCameraTracksFromStream(videoStream);
        await meeting.unpublishStreams([videoStream]);
      }

      const videoStream1 = await webex.meetings.mediaHelpers.createCameraStream(videoConstraints);
      registerTracks(videoStream1, activeCameraTracks);
      await meeting.publishStreams({ camera: videoStream1 });

      set({
        videoStream: videoStream1,
        localVideo: videoStream1?.outputStream || null,
      });
    } catch (err) {
      logger.error('Failed to switch camera:', err);
    }
  },

  // Use the param, not a stale localStorage read; drain old mic tracks first.
  switchMicrophone: async (deviceId: string) => {
    const { meeting, audioStream, setAudioDeviceId } = get();
    if (!meeting) {
      logger.warn('No meeting found to switch microphone');
      return;
    }
    try {
      setAudioDeviceId(deviceId);
      localStorage.setItem('preferredAudioDeviceId', deviceId);

      const audioConstraints = {
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
        deviceId: { exact: deviceId },
      };

      if (audioStream) {
        stopAllMicTracksFromStream(audioStream);
        drainAndStop(activeMicrophoneTracks);
        await meeting.unpublishStreams([audioStream]);
      }

      const microphoneStream =
        await meeting.webex.meetings.mediaHelpers.createMicrophoneStream(audioConstraints);

      registerTracks(microphoneStream, activeMicrophoneTracks);
      if (get().isAudioEnabled) {
        await meeting.publishStreams({ microphone: microphoneStream });
      }

      set({ audioStream: microphoneStream });
    } catch (err) {
      logger.error('Failed to switch microphone:', err);
    }
  },

  connectJitsiForScreenShare: async () => {
    logger.info('🚀 [Jitsi] Connecting for screen sharing...');
    const connectGeneration = _jitsiScreenShareGeneration;
    const { isJitsiConnected, jitsiService: storedService, room } = get();

    if (isJitsiConnected && storedService) {
      const isJoined = storedService.getRoomState()?.isJoined;
      logger.info(`✅ [Jitsi] Already connected (isJoined: ${isJoined})`);
      if (isJoined) {
        return;
      } else {
        logger.warn('⚠️ [Jitsi] Connected but not joined, cleaning up and reconnecting');
        await get().disconnectJitsiScreenShare();
      }
    }

    if (!room) {
      throw new Error('No Nexgen room for screen share');
    }

    try {
      // Keyed by Nexgen room: a meeting-link prefix collided across every room on the site.
      const webexMeetingId = room.toLowerCase();

      logger.info(`🔑 [Jitsi] Screen-share room for: ${webexMeetingId}`);

      logger.info('📡 [Jitsi] Requesting token from backend...');
      const { participants } = get();

      const selfMemberId = participants.find((p: any) => p.isSelf)?.id;
      const params = new URLSearchParams({ meetingId: webexMeetingId });
      if (selfMemberId) params.set('webex_member_id', selfMemberId);
      const tokenEndpoint = `meeting/webex-screenshare/token?${params}`;

      logger.info(`🔑 [Jitsi] Self WebEx memberId for token: ${selfMemberId ?? 'not found'}`);
      const result = await ApiService.request<any>({
        endpoint: tokenEndpoint,
      });

      const { roomId: jitsiRoomId, token: jitsiToken } = result;

      if (!jitsiRoomId || !jitsiToken) {
        throw new Error('Invalid Jitsi credentials from backend');
      }

      logger.info(`✅ [Jitsi] Got credentials - room: ${jitsiRoomId}`);

      const service = initializeWebexJitsiMeetingService();

      const subscription = service.events$.subscribe((event: any) => {
        logger.debug(`🎯 [Jitsi] Event: ${event.type}`, event);

        if (event.type === 'CONFERENCE_JOINED') {
          logger.info('🎉 [Jitsi] Conference JOINED successfully!');
        } else if (event.type === 'CONFERENCE_FAILED') {
          logger.error('💥 [Jitsi] Conference FAILED:', event.payload);
        } else if (event.type === 'CONNECTION_ESTABLISHED') {
          logger.info('🔗 [Jitsi] Connection ESTABLISHED');
        } else if (event.type === 'CONNECTION_FAILED') {
          logger.error('💥 [Jitsi] Connection FAILED:', event.payload);
        }

        if (event.type === JitsiConferenceEvents.USER_LEFT) {
          const user = event.payload;
          const webexMemberId = user?._identity?.user?.webex_member_id;
          logger.info('[Webex] [Jitsi] USER_LEFT — webex_member_id:', webexMemberId);

          if (webexMemberId) {
            const { participants, remoteVideos } = get();
            const matched = participants.find((p: any) => p.id === webexMemberId);
            if (matched) {
              logger.info('[Webex] [Jitsi] Removing WebEx participant by ID:', webexMemberId);
              set({
                participants: participants.filter((p: any) => p.id !== webexMemberId),
                remoteVideos: remoteVideos.filter((v: any) => v.participantId !== webexMemberId),
              });
            } else {
              logger.warn(
                '[Webex] [Jitsi] No WebEx participant matched webex_member_id:',
                webexMemberId
              );
            }
          }
        }

        if (event.type === JitsiConferenceEvents.TRACK_ADDED) {
          const track = event.payload;
          logger.info(`🔔 [Jitsi] TRACK_ADDED:`, {
            trackType: event.trackType,
            participantId: event.participantId,
            videoType: track?.videoType,
            type: track?.getType?.(),
          });
        }

        const isScreenShareTrack =
          event.type === JitsiConferenceEvents.TRACK_ADDED &&
          (event.trackType === 'screen' ||
            event.payload?.videoType === 'desktop' ||
            (event.payload?.getType?.() === 'video' && event.payload?.videoType === 'desktop'));

        if (isScreenShareTrack) {
          const track = event.payload;
          if (!track) return;

          const participantId = event.participantId || 'unknown';
          const isLocal = participantId === service.getMyUserId();

          logger.info(
            `📺 [Jitsi] Screen share ${isLocal ? 'LOCAL' : 'REMOTE'} added:`,
            participantId
          );

          let stream: MediaStream | null = null;
          try {
            stream = track.getOriginalStream?.() || track.getStream?.();
            if (!stream && track.getTrack) {
              stream = new MediaStream([track.getTrack()]);
            }
          } catch (err) {
            logger.error('Error extracting stream from track:', err);
            return;
          }

          if (!stream) {
            logger.warn('Could not extract stream from track');
            return;
          }

          const nativeTrack = track.getTrack?.();
          if (nativeTrack) {
            logger.info(`👂 [Jitsi] Setting up 'ended' listener for track:`, {
              participantId,
              trackId: nativeTrack.id,
              trackKind: nativeTrack.kind,
              initialReadyState: nativeTrack.readyState,
            });

            nativeTrack.addEventListener('ended', () => {
              logger.info(`🔚 [Jitsi] ⚠️ NATIVE TRACK ENDED EVENT FIRED:`, {
                participantId,
                trackId: nativeTrack.id,
                currentReadyState: nativeTrack.readyState,
              });

              const { remoteScreenShares, jitsiScreenShareCount } = get();
              const removedShare = remoteScreenShares.find(
                (s) => s.participantId === participantId
              );
              const filtered = remoteScreenShares.filter((s) => s.participantId !== participantId);

              logger.info(`🔚 [Jitsi] Removing via 'ended' event:`, {
                participantId,
                before: remoteScreenShares.length,
                after: filtered.length,
              });

              set({
                remoteScreenShares: filtered,
                jitsiScreenShareCount: removedShare?.isLocal
                  ? jitsiScreenShareCount
                  : Math.max(0, jitsiScreenShareCount - 1),
              });
              logger.info(`✅ [Jitsi] Screen share removed via 'ended' event:`, participantId);
            });
          } else {
            logger.warn(`⚠️ [Jitsi] Could not get native track for participant:`, participantId);
          }

          const { remoteScreenShares, jitsiScreenShareCount } = get();
          if (!remoteScreenShares.find((s) => s.participantId === participantId)) {
            set({
              remoteScreenShares: [...remoteScreenShares, { stream, participantId, isLocal }],
              jitsiScreenShareCount: isLocal ? jitsiScreenShareCount : jitsiScreenShareCount + 1,
            });
            logger.info(`✅ [Jitsi] Screen share added to state:`, {
              participantId,
              isLocal,
              hasStream: !!stream,
            });
          }
        }

        const isTrackMuteChanged =
          event.type === 'conference.trackMuteChanged' && event.trackType === 'screen';

        if (isTrackMuteChanged) {
          const track = event.payload;
          const participantId = track?.getParticipantId?.() || track?.participantId || 'unknown';
          const isMuted = event.isMuted;

          if (isMuted === false) {
            logger.info(`🎬 [Jitsi] Screen share UNMUTED (started) by participant:`, participantId);

            const nativeTrack = track?.stream?.getTracks?.()?.[0];
            const stream = nativeTrack ? new MediaStream([nativeTrack]) : null;

            if (!stream) {
              logger.warn(
                `⚠️ [Jitsi] Could not create stream from unmuted track for participant:`,
                participantId
              );
              return;
            }

            const { remoteScreenShares, jitsiScreenShareCount } = get();
            const myParticipantId = get().jitsiService?.getMyUserId();
            const isLocal = participantId === myParticipantId;

            if (!remoteScreenShares.find((s) => s.participantId === participantId)) {
              set({
                remoteScreenShares: [...remoteScreenShares, { stream, participantId, isLocal }],
                jitsiScreenShareCount: isLocal ? jitsiScreenShareCount : jitsiScreenShareCount + 1,
              });
              logger.info(`✅ [Jitsi] Screen share added via unmute:`, { participantId, isLocal });
            }
          } else if (isMuted === true) {
            logger.info(`🛑 [Jitsi] Screen share MUTED (stopped) by participant:`, participantId);

            const { remoteScreenShares, jitsiScreenShareCount } = get();
            const removedShares = remoteScreenShares.filter(
              (s) => s.participantId === participantId
            );

            if (removedShares.length > 0) {
              const updatedShares = remoteScreenShares.filter(
                (s) => s.participantId !== participantId
              );
              const isLocal = removedShares.some((s) => s.isLocal);

              set({
                remoteScreenShares: updatedShares,
                jitsiScreenShareCount: isLocal
                  ? jitsiScreenShareCount
                  : Math.max(0, jitsiScreenShareCount - removedShares.length),
              });

              logger.info(
                `✅ [Jitsi] Removed ${removedShares.length} screen share(s) from participant ${participantId}. Remaining: ${updatedShares.length}`
              );
            } else {
              logger.warn(
                `⚠️ [Jitsi] Screen share muted but participant ${participantId} not found in remoteScreenShares`
              );
            }
          }
        }
      });

      let joinSub: { unsubscribe: () => void } | null = null;
      let joinTimeout: ReturnType<typeof setTimeout> | null = null;

      const conferenceJoinedPromise = new Promise<void>((resolve, reject) => {
        joinTimeout = setTimeout(() => {
          if (joinSub) joinSub.unsubscribe();
          reject(new Error('Conference join timeout after 15 seconds'));
        }, 15000);

        joinSub = service.events$.subscribe((event: any) => {
          if (
            event.type === JitsiConferenceEvents.CONFERENCE_JOINED ||
            event.type === 'CONFERENCE_JOINED'
          ) {
            if (joinTimeout) clearTimeout(joinTimeout);
            if (joinSub) joinSub.unsubscribe();
            resolve();
          } else if (
            event.type === JitsiConferenceEvents.CONFERENCE_FAILED ||
            event.type === 'CONFERENCE_FAILED'
          ) {
            if (joinTimeout) clearTimeout(joinTimeout);
            if (joinSub) joinSub.unsubscribe();
            reject(new Error('Conference join failed'));
          }
        });
      });

      const isConnected = service.isConnected();

      if (isConnected) {
        logger.info('✅ [Jitsi] Already connected, joining directly');
        await service.joinMeeting(jitsiRoomId, {
          token: jitsiToken,
          createLocalTracks: false,
        });
      } else {
        logger.info('🔌 [Jitsi] Initializing connection...');
        await service.initialize({ roomConfig: { roomId: jitsiRoomId } }, jitsiToken);
        logger.info('✅ [Jitsi] Service initialized');

        logger.info('⏳ [Jitsi] Waiting for connection to establish...');
        const startTime = Date.now();
        const timeout = 10000;
        const checkInterval = 100;
        let connectionEstablished = false;

        while (Date.now() - startTime < timeout) {
          if (service.isConnected()) {
            connectionEstablished = true;
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, checkInterval));
        }

        if (connectionEstablished) {
          logger.info('🚪 [Jitsi] Connection established, joining conference...');
          await service.joinMeeting(jitsiRoomId, {
            token: jitsiToken,
            createLocalTracks: false,
          });
        } else {
          logger.warn('⚠️ [Jitsi] Connection timeout, retrying join...');
          await service.joinMeeting(jitsiRoomId, {
            token: jitsiToken,
            createLocalTracks: false,
          });
        }
      }

      logger.info('⏳ [Jitsi] Waiting for CONFERENCE_JOINED event...');
      await conferenceJoinedPromise;
      logger.info('🎊 [Jitsi] CONFERENCE_JOINED event received!');

      const roomState = service.getRoomState();
      logger.info('📊 [Jitsi] Final room state:', roomState);

      // A teardown ran mid-connect — leave the room we just joined, don't adopt it.
      if (connectGeneration !== _jitsiScreenShareGeneration) {
        logger.warn('[Jitsi] Screen-share join landed after a teardown — leaving it again');
        subscription.unsubscribe();
        await service.leaveMeeting({ killConnection: true }).catch((error: any) => {
          logger.warn('[Jitsi] Leaving the superseded screen-share conference failed', error);
        });
        await resetWebexJitsiMeetingService();
        return;
      }

      set({
        jitsiService: service,
        jitsiSubscription: subscription,
        isJitsiConnected: true,
      });

      logger.info('✅ [Jitsi] Connected successfully for screen sharing');
    } catch (error) {
      logger.error('❌ [Jitsi] Connection failed:', error);
      const { jitsiSubscription: sub } = get();
      if (sub) sub.unsubscribe();
      set({
        jitsiService: null,
        jitsiSubscription: null,
        isJitsiConnected: false,
      });
      throw error;
    }
  },

  disconnectJitsiScreenShare: async () => {
    const { jitsiSubscription, jitsiService: storedJitsiService } = get();
    // Supersede any connect still in flight (see _jitsiScreenShareGeneration).
    _jitsiScreenShareGeneration += 1;

    // Mid-connect, only the singleton knows the conference — the store adopts it later.
    const service = storedJitsiService ?? webexJitsiService;
    if (!service) {
      logger.debug('[Jitsi] No service to disconnect');
      return;
    }
    if (!storedJitsiService) {
      logger.warn('[Jitsi] Disconnecting a screen-share service the store never adopted');
    }

    try {
      if (jitsiSubscription) {
        jitsiSubscription.unsubscribe();
      }
      await service.leaveMeeting({ killConnection: true });
      logger.info('✅ [Jitsi] Disconnected');
    } catch (error) {
      logger.error('❌ [Jitsi] Disconnect error:', error);
    } finally {
      // Unconditional — a failed leave must not leave stale service state for the next connect.
      await resetWebexJitsiMeetingService().catch((error: any) =>
        logger.warn('[Jitsi] Screen-share service reset failed', error)
      );
      set({
        jitsiService: null,
        jitsiSubscription: null,
        isJitsiConnected: false,
        jitsiScreenShareCount: 0,
        remoteScreenShares: [],
      });
    }
  },

  toggleScreenShare: async () => {
    const { meeting, isScreenSharing } = get();

    if (!meeting) {
      logger.debug('toggleScreenShare :: no valid meeting object!');
      return;
    }

    try {
      const currentJitsiService = initializeWebexJitsiMeetingService();
      const { isJitsiConnected } = get();

      if (isScreenSharing) {
        logger.info('🛑 [Jitsi] Stopping screen share...');

        const participantId = currentJitsiService?.getMyUserId();
        const jitsiRoomName = (currentJitsiService as any)?.conference?.getName?.();
        const storeRoomId = get().roomId;
        const meetingRoomId = get().meetingInfo?.id;
        const roomId = jitsiRoomName || storeRoomId || meetingRoomId || get().room;

        logger.info(`🛑 [Jitsi] Stop info - participantId: ${participantId}, roomId: ${roomId}`);

        if (currentJitsiService) {
          try {
            await currentJitsiService.stopScreenShare();
            logger.info('✅ [Jitsi] Screen share stopped in Jitsi service');
          } catch (error) {
            logger.error('❌ [Jitsi] Error stopping screen share:', error);
          }
        }

        const currentShares = get().remoteScreenShares;
        const myShares = currentShares.filter((s) => s.participantId === participantId);

        if (myShares.length > 0) {
          logger.info(`🧹 [Jitsi] Removing ${myShares.length} local screen share(s)`);
          const updatedShares = currentShares.filter((s) => s.participantId !== participantId);
          set({
            remoteScreenShares: updatedShares,
            jitsiScreenShareCount: Math.max(0, get().jitsiScreenShareCount - myShares.length),
            isScreenSharing: false,
          });
        } else {
          set({ isScreenSharing: false });
        }

        logger.info('✅ [Jitsi] Screen share stopped completely');
      } else {
        logger.info('🎬 [Jitsi] Starting screen share...');

        if (!isJitsiConnected) {
          logger.info('🔌 [Jitsi] Not connected, connecting now...');
          await get().connectJitsiForScreenShare();
          logger.info('✅ [Jitsi] Connected and joined successfully');
        } else {
          logger.info('✅ [Jitsi] Already connected, skipping connection');
        }

        // Callback updates store state when the browser stops the share.
        logger.info('🎥 [Jitsi] Calling startScreenShare()...');
        await currentJitsiService.startScreenShare(() => {
          const participantId = currentJitsiService?.getMyUserId();
          const { remoteScreenShares, jitsiScreenShareCount } = get();
          const updatedShares = remoteScreenShares.filter((s) => s.participantId !== participantId);
          const removedCount = remoteScreenShares.length - updatedShares.length;
          logger.info('🛑 [Jitsi] Browser stopped screen share, cleaning up store state');
          set({
            isScreenSharing: false,
            remoteScreenShares: updatedShares,
            jitsiScreenShareCount: Math.max(0, jitsiScreenShareCount - removedCount),
          });
        });

        set({ isScreenSharing: true });
        logger.info('✅ [Jitsi] Screen share started successfully');
      }
    } catch (error) {
      logger.error('❌ [Jitsi] Screen sharing error:', error);
      set({ isScreenSharing: false });
    }
  },

  acquireGuestJoinInfo: async (roomId: string) => {
    try {
      // ApiService, not raw fetch: for a guest it omits Authorization, sets withCredentials so
      // the httpOnly guestToken cookie rides along, and attaches x-org-pid.
      const result = await ApiService.post<{
        meeting: any;
        accessToken: string;
        accessTokenExpiresAt: string;
        displayName: string;
      }>('apps/webex/meeting/guest/join', { roomId }, { userType: UserType.GuestUser });

      if (!result?.meeting?.webLink || !result?.accessToken) {
        logger.warn('[guest] guest/join returned an unusable payload');
        set({ guestJoinUnavailable: true, guestJoinUnavailableReason: 'malformed-response' });
        return { ok: false, reason: 'malformed-response' };
      }

      set({
        personalMeeting: result.meeting,
        guestToken: result.accessToken,
        guestJoinUnavailable: false,
        guestJoinUnavailableReason: null,
        guestNoMeeting: false,
      });
      return { ok: true };
    } catch (error: any) {
      // ApiService rethrows the response BODY, not the axios error, so {status, message, code}
      // sits on the thrown value — error.response.status is undefined here. `code` is ours;
      // `statusCode` is what a Nest NotFoundException serialises.
      const status = error?.statusCode ?? error?.status ?? error?.response?.status;
      const reason = String(error?.code ?? status ?? 'network-error');

      // 404 = the placement room has no live meeting (a lobby/default room, or an expired
      // meeting). That is a valid arrival, not a failure: the guest lands in the room view
      // without a Webex join, and a later summon into a meeting room re-runs this join.
      // Setting guestJoinUnavailable here would surface the fallback panel and arm the retry
      // effect for something that can never succeed — a summoned guest would look broken.
      if (status === 404) {
        logger.info('[guest] No Webex meeting for the current placement — arriving without a join');
        // Drives the slow bounded retry in GuestWebExView: the guest joins automatically
        // once the host starts the meeting in this room.
        set({ guestNoMeeting: true });
        return { ok: false, reason: 'no-meeting' };
      }

      logger.warn(`[guest] in-app Webex join unavailable (${reason})`);
      set({
        guestJoinUnavailable: true,
        guestJoinUnavailableReason: reason,
        guestNoMeeting: false,
      });
      return { ok: false, reason };
    }
  },

  admitToRoom: async (roomId: string, token: string, participantIds: string[]) => {
    try {
      const url = `${API_ROOT}/apps/webex/meeting/admit?roomId=${encodeURIComponent(roomId)}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ participantIds }),
      });
      if (!response.ok) throw new Error(`Failed to admit participants: ${response.status}`);
      const result = await response.json();
      logger.debug('Admit result:', result);
      return result;
    } catch (error) {
      logger.error('Error admitting participants:', error);
      throw error;
    }
  },

  admitEveryoneInLobby: async (roomId: string, token: string) => {
    try {
      const url = `${API_ROOT}/apps/webex/meeting/admit-everyone?roomId=${encodeURIComponent(roomId)}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`Failed to admit participants: ${response.status}`);
      const result = await response.json();
      logger.debug('Admit result:', result);
      return result;
    } catch (error) {
      logger.error('Error admitting participants:', error);
    }
  },

  fetchPersonalMeeting: async (roomId: string, token: string) => {
    // Always hits the backend — the create-meeting call also ends any other active
    // meeting this caller still owns (see backend endOtherActiveMeetingsForCaller), a
    // safety side effect that must run on every room switch, not just a cacheable fetch.
    const url = `${API_ROOT}/apps/webex/meeting/create?roomId=${roomId}&access_token=${token}`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      // Surface the backend's (Webex's) reason — the bare status code hides it.
      const reason = await response
        .json()
        .then((body: any) => body?.message ?? body?.error)
        .catch(() => null);
      throw new Error(
        `Failed to fetch personal meeting: ${response.status}${reason ? ` — ${reason}` : ''}`
      );
    }
    const result = await response.json();
    if (!result.data) throw new Error('Invalid personal meeting response');
    set({ personalMeeting: result.data });
    return result.data;
  },

  toggleVideo: async () => {
    const { meeting, isVideoEnabled, videoStream } = get();

    try {
      if (!meeting) return;

      if (isVideoEnabled) {
        if (videoStream) {
          await meeting.unpublishStreams([videoStream]);
        }
        if (videoStream) {
          stopAllCameraTracksFromStream(videoStream);
        }
        drainAndStop(activeCameraTracks);
        set({ isVideoEnabled: false, localVideo: null, videoStream: null });
      } else {
        const videoConstraints = {
          width: 640,
          height: 480,
          frameRate: { ideal: 30 },
          deviceId: localStorage.getItem('preferredVideoDeviceId')
            ? { exact: localStorage.getItem('preferredVideoDeviceId') }
            : undefined,
        };

        const newVideoStream =
          await meeting.webex.meetings.mediaHelpers.createCameraStream(videoConstraints);

        registerTracks(newVideoStream, activeCameraTracks);
        await meeting.publishStreams({ camera: newVideoStream });

        set({
          isVideoEnabled: true,
          videoStream: newVideoStream,
          localVideo: newVideoStream.outputStream,
        });
      }
    } catch (error) {
      logger.error('Error toggling video:', error);
      throw error;
    }
  },

  // Mute unpublishes but keeps tracks alive so unmute re-publishes instantly.
  toggleAudio: async () => {
    const { meeting, isAudioEnabled } = get();
    try {
      if (!meeting) return;

      // Resolve the live stream from state or the SDK's mediaProperties.
      const liveStream =
        get().audioStream ||
        meeting.mediaProperties?.audioStream ||
        meeting.mediaProperties?.microphone ||
        null;

      const audioConstraints = {
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
        deviceId: localStorage.getItem('preferredAudioDeviceId')
          ? { exact: localStorage.getItem('preferredAudioDeviceId') }
          : undefined,
      };

      if (isAudioEnabled) {
        if (liveStream) {
          try {
            await meeting.unpublishStreams([liveStream]);
          } catch (_) {
            /* ignore */
          }
        }
        set({ isAudioEnabled: false });
        logger.info('[toggleAudio] Microphone muted (tracks kept alive)');
      } else {
        // Re-publish the same stream while its tracks are still live.
        let streamToPublish = liveStream;
        const tracksAlive = !!liveStream
          ?.getTracks?.()
          .some?.((t: MediaStreamTrack) => t.readyState === 'live');

        if (!tracksAlive) {
          // Stream was stopped elsewhere — acquire a fresh one.
          streamToPublish =
            await meeting.webex.meetings.mediaHelpers.createMicrophoneStream(audioConstraints);
          registerTracks(streamToPublish, activeMicrophoneTracks);
        }

        try {
          await meeting.publishStreams({ microphone: streamToPublish });
        } catch (publishError) {
          // SDK may reject re-publishing a known stream — fall back to a fresh one.
          if (tracksAlive) {
            try {
              const freshStream =
                await meeting.webex.meetings.mediaHelpers.createMicrophoneStream(audioConstraints);
              registerTracks(freshStream, activeMicrophoneTracks);
              await meeting.publishStreams({ microphone: freshStream });
              streamToPublish = freshStream;
            } catch (secondError) {
              stopAllMicTracksFromStream(streamToPublish);
              set({ isAudioEnabled: false, audioStream: null });
              throw secondError;
            }
          } else {
            // Don't leak a mic we failed to publish; keep state consistent (muted).
            stopAllMicTracksFromStream(streamToPublish);
            set({ isAudioEnabled: false, audioStream: null });
            throw publishError;
          }
        }

        set({ isAudioEnabled: true, audioStream: streamToPublish });
        logger.info('[toggleAudio] Microphone unmuted and publishing');
      }
    } catch (error) {
      logger.error('Error toggling audio:', error);
      throw error;
    }
  },

  reInitializeWebex: async () => {
    const { room, token, isGuestSession } = get();
    const meetingPin = import.meta.env?.VITE_WEBEX_MEETING_PIN || '123456';
    await get().initializeWebex(token, room, meetingPin, isGuestSession, true);
  },

  initializeWebex: async (
    access_token: string,
    meetingId: string,
    pin: string = '',
    isGuest: boolean = false,
    reinitialize: boolean = false
  ) => {
    // A later run supersedes this one; abort at every await when stale.
    const generation = ++_webexInitGeneration;
    _webexInitTargetRoom = meetingId;
    const isStale = () => generation !== _webexInitGeneration;
    // Keep isJoinPending until `room` is set, so the overlay stays open on a first join.
    const hadJoinFeedback = get().isJoinPending;
    if (hadJoinFeedback) set({ isStartingMeeting: true });

    try {
      // A settle cleanup from an expired reboot wait may still be mid-flight — its final
      // resetState would wipe whatever this join builds. Finish it before touching state.
      if (_settleCleanup) {
        await _settleCleanup;
        if (isStale()) return;
      }

      const { room, token, isInMeeting } = get();
      if (!reinitialize) {
        if (meetingId === '' || !meetingId || access_token === '' || !access_token) {
          if (hadJoinFeedback) {
            set({ isJoinPending: false, isStartingMeeting: false, joinStage: '' });
          }
          return;
        }
        // For a guest `access_token` is the sentinel and `token` the real credential, so the
        // equality check can never match — compare the room instead.
        if (room === meetingId && isInMeeting && (isGuest || token === access_token)) {
          // Already in this meeting — clear any stale starting flag (e.g. left set by a
          // superseded run) so the top-bar spinner can't outlive the join.
          if (get().isStartingMeeting || get().isJoinPending) {
            set({ isStartingMeeting: false, isJoinPending: false });
          }
          return;
        }
      }

      // Show the connecting overlay immediately on click, not after the network round
      // trips below — leave/cleanup/fetchPersonalMeeting can take a visible moment, and
      // the overlay's whole point is to block interaction for that entire window.
      set({ isStartingMeeting: true, isGuestSession: isGuest });
      get().setJoinStage('preparing');

      // Leave the previous room's meeting completely before doing anything for the new
      // one, and fetch the new room's meeting only after that leave — the fetch's network
      // round trip then doubles as settle time for Webex's concurrent-limit tracker, so
      // the new join fires as late after the leave as possible.
      // Not gated on get().meeting — a mid-join switch can lose the store reference while
      // the SDK still holds the locus; leaveMeeting sweeps whatever it holds.
      if (!reinitialize) {
        if (get().room !== meetingId) await get().leaveMeeting(false);
      } else {
        await get().leaveMeeting(false);
      }
      if (isStale()) {
        logger.debug('[initializeWebex] Superseded before cleanup, aborting', { meetingId });
        return;
      }

      // disposeWebex=false: keep the SDK instance alive across a room switch for a faster
      // rejoin — only hangup / popup close / auth loss fully dispose it.
      await get().cleanup(false);
      if (isStale()) {
        logger.debug('[initializeWebex] Superseded during cleanup, aborting', { meetingId });
        return;
      }
      // cleanup() always clears joinStage — restore it so the overlay's step list doesn't
      // briefly show nothing active during the leave/cleanup window.
      get().setJoinStage('preparing');

      // Resolve the credential the SDK is initialized with. For a guest the `access_token`
      // argument is only a sentinel — the real token comes from acquireGuestJoinInfo.
      let effectiveToken = access_token;

      if (isGuest) {
        const guestJoin = await get().acquireGuestJoinInfo(meetingId);
        if (isStale()) {
          logger.debug('[initializeWebex] Superseded after acquireGuestJoinInfo, aborting', {
            meetingId,
          });
          return;
        }
        if (!guestJoin.ok) {
          // Bow out without touching the SDK — GuestWebExView renders the external-link fallback.
          set({ isStartingMeeting: false, isJoinPending: false, joinStage: '' });
          return;
        }
        effectiveToken = get().guestToken ?? access_token;
        _webexInitTargetLink = get().personalMeeting?.webLink ?? null;
      } else {
        // Same-room rejoin (reboot or reconnect): the meeting link cannot have changed and
        // the fetch's create-meeting side effect (ending the caller's other meetings) has
        // nothing to do here — so skip the backend round trip and reuse the cached meeting.
        // BUT the backend create is also what refreshes the row's expires_at once it lapses
        // (a stale row 404s GUESTS on guest/join even while the host joins fine — observed
        // mid-reboot on long sessions), so the cache is trusted only while the expiry is
        // still in the future. Compare against serverNow, not the client clock: the backend
        // returns both for exactly this purpose.
        const cachedPersonalMeeting = get().personalMeeting;
        const cacheStillFresh =
          cachedPersonalMeeting?.expiresAt &&
          cachedPersonalMeeting?.serverNow &&
          new Date(cachedPersonalMeeting.expiresAt).getTime() >
            new Date(cachedPersonalMeeting.serverNow).getTime();
        const targetMeeting =
          reinitialize &&
          cacheStillFresh &&
          cachedPersonalMeeting?.webLink &&
          get().room === meetingId
            ? cachedPersonalMeeting
            : await get().fetchPersonalMeeting(meetingId, access_token);
        if (isStale()) {
          logger.debug('[initializeWebex] Superseded after fetchPersonalMeeting, aborting', {
            meetingId,
          });
          return;
        }
        _webexInitTargetLink = targetMeeting?.webLink ?? null;
      }

      set({
        room: meetingId,
        isJoinPending: false,
        token: effectiveToken,
        // Floor hosting the meeting — gates caption processing.
        webexFloorPid: useWorkplaceStore.getState().currentRoom?.floor_pid || '',
      });
      if (!window.Webex) throw new Error('Webex SDK not loaded');

      get().setJoinStage('connecting');

      // Reuse the SDK instance across room switches — Webex.init() + device registration
      // + mercury connect are the expensive part of a join. Fresh bootstrap only on first
      // join, after hangup, or if the existing instance is no longer registered.
      let webexInstance = get().webex;
      // `token` is what the live instance was built with (destructured before the set() above).
      // Re-init when it differs, so an SDK bound to another identity is never reused.
      if (!webexInstance || !webexInstance.meetings?.registered || token !== effectiveToken) {
        const config = {
          credentials: { access_token: effectiveToken },
          meetings: {
            deviceType: 'WEB',
            enableMultiStream: true,
            enableRtx: true,
            // Auto-connect the LLM websocket — startTranscription() fails without it.
            enableAutomaticLLM: true,
            logging: { enable: true, verboseEvents: true },
            experimental: { enableUnifiedMeetings: true },
            // autoRejoin MUST stay false. On a locus disconnect (meeting ended for all, or
            // server-side inactivity) the SDK branches on this flag: true silently rejoins
            // the locus itself — racing our stop-recording reboot flow, which then finds
            // isInMeeting still true and no-ops — while false delivers meeting:self:left,
            // the event the ejection handling below depends on. ICE-level media recovery is
            // unaffected by this flag.
            reconnection: { enabled: true, autoRejoin: false },
          },
          logger: { level: 'debug', historyLength: 1000 },
        };

        webexInstance = window.Webex.init(config);

        await Promise.race([
          new Promise<void>((resolve) => {
            webexInstance.once('ready', () => resolve());
          }),
          new Promise<void>((_, reject) =>
            setTimeout(() => reject(new Error('Webex initialization timeout')), 30000)
          ),
        ]);

        if (isStale()) {
          logger.debug('[initializeWebex] Superseded after SDK ready, aborting', { meetingId });
          return;
        }

        set({ webex: webexInstance, isConnected: true });
        await webexInstance.meetings.register();
        if (isStale()) {
          logger.debug('[initializeWebex] Superseded after register, aborting', { meetingId });
          return;
        }
      }

      // Bounded retry with exponential backoff: Webex's concurrent-meeting-limit
      // bookkeeping is eventually consistent with the leave that just completed and
      // has no SDK signal for when it catches up, so a first join attempt can still
      // 423 and then succeed moments later. Retrying against the actual failure adapts
      // to however long Webex takes, instead of guessing with a fixed pre-join sleep.
      // Capped so a genuinely unfixable failure (and the connecting modal, which is up
      // for the whole isStartingMeeting window) doesn't hang around indefinitely.
      const MAX_JOIN_ATTEMPTS = 4;
      // The owner's concurrent-meeting-limit (423 / errorCode 2423012) bookkeeping can lag
      // longer than the generic budget covers on a fast room switch — extra attempts, still
      // bounded so the connecting modal can't hang forever.
      // The eventually-consistent lag (a just-ended previous meeting) resolves within a
      // couple of attempts. A slot genuinely held elsewhere — the same account hosting a
      // meeting in another tab/app/device — never frees in any retry window, so fail fast
      // and tell the user instead of spinning the overlay for minutes.
      const MAX_CONCURRENCY_ATTEMPTS = 3;
      // Retiring the room's meeting mints a brand new one, so it gets exactly one shot per
      // join — a mismatched error signature would otherwise burn a fresh Webex meeting on
      // every attempt of an unrelated failure.
      let retiredDeadMeeting = false;
      for (let attempt = 1; ; attempt++) {
        try {
          // Pass the instance so a concurrent cleanup can't null it between calls.
          await get().createMeeting(webexInstance);
          if (isStale()) {
            logger.debug('[initializeWebex] Superseded after createMeeting, aborting', {
              meetingId,
            });
            return;
          }

          // The real host key (createMeeting put it on meetingInfo) is what makes the
          // moderator join grant host; env constant is the fallback for key-less sites.
          const meetingPin = import.meta.env?.VITE_WEBEX_MEETING_PIN || '123456';
          const hostKey = get().meetingInfo?.hostKey || pin || meetingPin;
          // Enable mic tracking before the SDK acquires the mic (cleanup reset it).
          _webexMicTrackingActive = true;
          await get().joinMeeting(hostKey);
          break;
        } catch (joinError: any) {
          const isConcurrencyLimit = isConcurrencyLimitError(joinError);
          const attemptLimit = isConcurrencyLimit ? MAX_CONCURRENCY_ATTEMPTS : MAX_JOIN_ATTEMPTS;
          if (isStale() || attempt >= attemptLimit) throw joinError;

          // The link the backend handed us looks dead. Backing off would just retry the same
          // dead link until the attempts run out, and the room would stay unjoinable for the
          // rest of the meeting's 40-minute window. Retire the row and re-fetch instead — the
          // backend then mints a fresh meeting, so the next attempt targets a different link.
          // Guests are excluded: they resolve meetings through the guest-join endpoint and
          // have no rights to discard the room's meeting.
          if (!retiredDeadMeeting && !isGuest && isMeetingGoneError(joinError)) {
            retiredDeadMeeting = true;
            logger.warn(
              `[join] Join attempt ${attempt}/${MAX_JOIN_ATTEMPTS} failed as if the meeting no longer exists — retiring it and re-fetching`,
              joinError
            );
            if (await retireRoomMeeting(meetingId)) {
              await get().fetchPersonalMeeting(meetingId, access_token);
              if (isStale()) {
                logger.debug('[initializeWebex] Superseded during dead-meeting recovery', {
                  meetingId,
                });
                return;
              }
              _webexInitTargetLink = get().personalMeeting?.webLink ?? null;
              // Straight to the retry: the link genuinely changed, so there is nothing to
              // wait out the way there is for the eventually-consistent 423 below.
              continue;
            }
          }

          const backoffMs = 1000 * 2 ** (attempt - 1);
          const headers = joinError?.headers ?? {};
          const retryAfter =
            (typeof headers.get === 'function' ? headers.get('retry-after') : undefined) ??
            headers['retry-after'] ??
            joinError?.retryAfter;
          logger.warn(
            `[initializeWebex] Join attempt ${attempt}/${MAX_JOIN_ATTEMPTS} failed, retrying in ${backoffMs}ms` +
              (retryAfter ? ` (Webex Retry-After: ${retryAfter})` : ''),
            joinError
          );
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
        }
      }

      // If superseded but the joined link still matches, adopt the call instead of leaving it.
      if (isStale()) {
        const joinedLink = get().meetingInfo?.meetingLink ?? null;
        if (joinedLink && _webexInitTargetLink && joinedLink !== _webexInitTargetLink) {
          logger.warn('[initializeWebex] Join completed for a superseded meeting, leaving it', {
            meetingId,
            joinedLink,
            wantedLink: _webexInitTargetLink,
          });
          await get().leaveMeeting(false);
          return;
        }

        logger.info('[initializeWebex] Superseded but joined the wanted meeting — adopting it', {
          meetingId,
          room: _webexInitTargetRoom,
        });
        set({ isStartingMeeting: false, room: _webexInitTargetRoom ?? meetingId });
        return webexInstance;
      }

      set({ isStartingMeeting: false, isJoinPending: false });
      return webexInstance;
    } catch (error) {
      logger.error('Webex initialization failed', error);

      // A concurrency-limit failure is almost always the same account hosting a meeting
      // elsewhere (another tab/app/device) — retrying can never free that slot, so tell the
      // user exactly what to do instead of leaving them with the generic hung-up state.
      if (isConcurrencyLimitError(error)) {
        useNotificationStore
          .getState()
          .showSnackbar(
            "You're already hosting a Webex meeting in another tab or device. End that meeting, then try joining again.",
            'error'
          );
      }

      // Only the newest run may clear flags on failure.
      if (!isStale()) {
        // isMeetingHungUp: true set here (and preserved through cleanup via keepHungUp)
        // so it never has a false window — CoreWebexAuth's room-change effect treats
        // isMeetingHungUp:false + isStartingMeeting:false as "auto-connect", and would
        // otherwise re-trigger this same failing join in a loop.
        clearRebootWait();
        set({
          isConnected: false,
          isStartingMeeting: false,
          isJoinPending: false,
          isInMeeting: false,
          joinStage: '',
          isMeetingHungUp: true,
          // Drop the overlay — the rejoin is not coming, so leave the user with the
          // normal hung-up UI and its manual Reconnect rather than a spinner forever.
          isRestarting: false,
          restartReason: null,
        });
        // The bounded retry above is exhausted (or this run is stale) — no further
        // auto-retry here, so a still-failing join (e.g. a genuine 423) doesn't keep
        // hammering Webex. Dispose for a clean next try.
        await get().cleanup(true, true);
      }
      throw error;
    }
  },

  createMeeting: async (webexInstance?: any) => {
    const state = get();
    const webex = webexInstance || state.webex;
    const { personalMeeting, meetingInfo: meetingInfomation } = state;

    if (!webex) throw new Error('Webex not initialized');
    if (!personalMeeting?.webLink) throw new Error('Personal meeting not available');

    try {
      const existingMeeting = webex.meetings.getMeetingByType(
        'MEETING_LINK',
        personalMeeting.webLink
      );
      let meeting = existingMeeting;

      if (existingMeeting) {
        try {
          await meeting.syncMeetings();
        } catch (syncError) {
          logger.warn('Meeting sync failed', syncError);
        }
      } else {
        console.log('Creating new meeting', personalMeeting.webLink);
        meeting = await webex.meetings.create(personalMeeting.webLink, 'MEETING_LINK');
      }

      if (!meeting?.id) throw new Error('Invalid meeting object returned');

      const meetingInfo: any = {
        id: meeting.id,
        sipUri: meetingInfomation?.sipUri || '',
        meetingLink: personalMeeting.webLink,
        // The backend's real key when the site issues one; the env constant never worked.
        hostKey: personalMeeting?.hostKey || import.meta.env?.VITE_WEBEX_MEETING_PIN || '123456',
        expiresAt: personalMeeting?.expiresAt,
        serverNow: personalMeeting?.serverNow,
      };

      set({ meetingInfo, meeting });
      return meetingInfo;
    } catch (error: any) {
      const isConflict =
        error.statusCode === 409 ||
        error.message?.includes('409') ||
        error.message?.includes('Conflict');

      if (isConflict) {
        const existingMeeting = webex.meetings.getMeetingByType(
          'MEETING_LINK',
          personalMeeting.webLink
        );
        if (existingMeeting) {
          const recoveredMeetingInfo = {
            id: existingMeeting.id,
            sipUri: meetingInfomation?.sipUri || '',
            meetingLink: personalMeeting.webLink,
            // The backend's real key when the site issues one; the env constant never worked.
            hostKey:
              personalMeeting?.hostKey || import.meta.env?.VITE_WEBEX_MEETING_PIN || '123456',
            expiresAt: personalMeeting?.expiresAt,
            serverNow: personalMeeting?.serverNow,
          };
          set({ meetingInfo: recoveredMeetingInfo, meeting: existingMeeting });
          return recoveredMeetingInfo;
        }
      }

      logger.error('Meeting creation failed', error);
      throw error;
    }
  },

  joinMeeting: async (hostPin?: string) => {
    // Captured once: re-reading at retry time would let a concurrent init flip guest-ness
    // between the failed join and the retry, and this gates the moderator path below.
    const { meeting, isGuestSession } = get();
    if (!meeting) throw new Error('No meeting available to join');

    try {
      // Re-arm the duplicate-leave guard before rejoining a recycled meeting.
      markMeetingJoined(meeting);
      setupMeetingEventListeners(meeting, { set, get });
      const media = await get().getMediaStreams();

      // Ask for moderator up front. This used to send moderator:false and only request the
      // Role on a joinIntentRequired retry — so whenever Webex accepted the plain join
      // (typical of the fresh instance a reboot cycle creates) the host returned as an
      // ordinary participant: selfRoles [], canStartRecording false, and the recording
      // button dead despite a paid licence. The retry below stays as the safety net for
      // the case where the SDK still demands explicit host intent.
      //
      // A guest must never present the host pin: VITE_WEBEX_MEETING_PIN is unset
      // everywhere, so '123456' is a well-known constant and a guest could escalate.
      // Declared here, not inside the try: the joinIntentRequired retry in the catch needs it.
      const canModerate = !!hostPin && !isGuestSession;
      // Real key vs the '123456' fallback — the reclaim below only runs for the former.
      const fallbackPin = import.meta.env?.VITE_WEBEX_MEETING_PIN || '123456';
      try {
        get().setJoinStage('joining');
        await meeting.join({
          enableMultistream: true,
          moderator: canModerate,
          ...(canModerate ? { pin: hostPin } : {}),
          moveToResource: false,
          resourceId: null,
          receiveTranscription: true,
          rejoin: true,
        });
      } catch (err: any) {
        if (err?.joinIntentRequired) {
          // The SDK signals "host intent required" as a failed first call and expects the
          // join to be re-issued — the retry is the handshake, not a params change (the
          // first attempt already carries the strongest intent we may send).
          await meeting.join({
            moderator: canModerate,
            ...(canModerate ? { pin: hostPin } : {}),
            enableMultistream: true,
            moveToResource: false,
            resourceId: null,
            receiveTranscription: true,
            rejoin: true,
          });
        } else {
          logger.error('Meeting join failed', err);
          throw err;
        }
      }

      // Re-arm AFTER the join too: a mid-join room switch issues a leave against a locus
      // we had not joined yet, which arms the guard and makes the teardown that runs once
      // this join lands get skipped as a duplicate.
      markMeetingJoined(meeting);

      // Some sites silently drop the join pin and let the user in as an attendee, so
      // reclaim host explicitly via assignRoles — a refusal here is a named error instead
      // of silence.
      if (canModerate && hostPin && hostPin !== fallbackPin) {
        const selfId = meeting?.members?.selfId;
        if (selfId) {
          try {
            await meeting.members.assignRoles(selfId, [
              { type: 'HOST', hasRole: true, hostKey: hostPin },
            ]);
            // The server accepted it — that IS the host role, whatever the local locus copy
            // still says. The flag lets recording gating fail open until the locus catches up.
            set({ webexHostReclaimed: true });

            // The local locus only updates from mercury deltas, which can be missed — re-pull
            // it so self roles and display hints refresh.
            try {
              await new Promise((resolve) => setTimeout(resolve, 500));
              await meeting?.webex?.meetings?.syncMeetings?.({
                keepOnlyLocusMeetings: false,
              });
              refreshRecordingHints(meeting);
            } catch (syncError: any) {
              logger.warn('[join] locus resync after reclaim failed', syncError);
            }
          } catch (reclaimError: any) {
            if (reclaimError?.code === 14) {
              // ReclaimHostIsHostAlready — the pin join already made us host. Fine.
              logger.info('[join] already host — reclaim not needed', { meetingId: meeting.id });
              set({ webexHostReclaimed: true });
            } else {
              logger.error('[join] host reclaim failed', {
                meetingId: meeting.id,
                code: reclaimError?.code,
                message: reclaimError?.sdkMessage ?? reclaimError?.message,
              });
            }
          }
        } else {
          logger.warn('[join] no self member id yet — cannot reclaim host', {
            meetingId: meeting.id,
          });
        }
      }

      const { room, token } = get();
      if (room && token) {
        try {
          await get().admitEveryoneInLobby(room, token);
          // Small settle window for admitted participants' tiles, not a fixed 1s tax on
          // every join — the admit call itself is already awaited.
          await new Promise((resolve) => setTimeout(resolve, 300));
        } catch (error) {
          logger.warn('Failed to admit participants', error);
        }
      }

      await meeting.addMedia({
        // addMedia publishes the passed streams during media connection setup.
        localStreams: {
          microphone: get().isAudioEnabled ? (media.microphone ?? undefined) : undefined,
          camera: media.camera ?? undefined,
        },
        // Audio is negotiated even when muted; video follows the camera state.
        videoEnabled: !!media.camera,
      });

      get().setJoinStage('media');

      try {
        await meeting.remoteMediaManager?.setLayout('ScreenShareView');
      } catch (error) {
        logger.debug('Error setting layout', error);
      }

      // isRestarting clears here rather than on the restart event: the overlay must stay up
      // for the whole cycle, right through the rejoin, not just until it starts.
      clearRebootWait();
      set({
        isInMeeting: true,
        isMeetingHungUp: false,
        isStartingMeeting: false,
        isRestarting: false,
        restartReason: null,
      });
      get().setJoinStage('');

      // Listen for caption events only while inside the Webex meeting.
      subscribeWebexLiveCaptions();

      // Webex Assistant transcription starts on demand from startLiveCaptions().
      logger.info(
        '[joinMeeting] Webex Assistant transcription supported for this meeting:',
        !!meeting.isTranscriptionSupported?.()
      );

      const { meetingInfo } = get();
      if (meetingInfo?.meetingLink?.includes('webex.com')) {
        logger.info('[NEX-817] Webex meeting detected, connecting to Jitsi for screen sharing...');
        // Fire-and-forget: Jitsi is only the screen-share side channel. Awaiting it here
        // held joinMeeting()'s resolution — and therefore isStartingMeeting=false — hostage
        // to a second backend round trip plus Jitsi connect, for nothing participant-visible.
        get()
          .connectJitsiForScreenShare()
          .then(() => logger.info('[NEX-817] ✅ Connected to Jitsi, ready for screen sharing'))
          .catch((jitsiError) => {
            logger.warn('[NEX-817] ⚠️ Failed to connect to Jitsi:', jitsiError);
          });
      } else {
        logger.debug('[NEX-817] Not a Webex meeting, skipping Jitsi connection');
      }

      return meeting;
    } catch (error: any) {
      // JoinMeetingError's own message is just "Error Joining Meeting" — the real cause
      // (e.g. 423 / 2423012 concurrent-limit) only lives in these fields.
      logger.error(
        `Error joining meeting — http=${error?.httpStatusCode ?? error?.statusCode ?? 'n/a'} ` +
          `sdk="${error?.sdkMessage ?? ''}" body=${JSON.stringify(error?.body ?? {})}`,
        error
      );
      // Only leave if the locus was actually created (state reached JOINED) — leaving
      // before that makes the SDK build a garbage URL and 404 against our own origin.
      if (meeting?.state === 'JOINED') {
        await leaveMeetingOnce(meeting).catch((leaveErr: any) => {
          if (isAlreadyLeftError(leaveErr)) return;
          if (leaveErr instanceof TypeError) {
            logger.debug('[joinMeeting] Post-failure leave crashed internally', leaveErr?.message);
            return;
          }
          logger.warn('[joinMeeting] Post-failure meeting leave failed', leaveErr);
        });
      } else {
        logger.debug(
          `[joinMeeting] Join failed before reaching JOINED (state=${meeting?.state}) — nothing to leave`
        );
        // No locus to leave — drop the local object so the later sweep can't try
        // to leave a garbage-URL locus.
        meeting?.webex?.meetings?.meetingCollection?.delete?.(meeting.id);
      }
      set({ meeting: null, isInMeeting: false, isMeetingHungUp: true });
      get().setJoinStage('');
      throw error;
    }
  },

  getMediaStreams: async () => {
    const { meeting, isVideoEnabled } = get();
    if (!meeting) throw new Error('No valid meeting object');

    try {
      const audioConstraints = {
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
        deviceId: localStorage.getItem('preferredAudioDeviceId')
          ? { exact: localStorage.getItem('preferredAudioDeviceId') }
          : undefined,
      };

      const microphoneStream =
        await meeting.webex.meetings.mediaHelpers.createMicrophoneStream(audioConstraints);

      registerTracks(microphoneStream, activeMicrophoneTracks);

      let cameraStream = null;
      if (isVideoEnabled) {
        const videoConstraints = {
          width: 640,
          height: 480,
          frameRate: { ideal: 30 },
          deviceId: localStorage.getItem('preferredVideoDeviceId')
            ? { exact: localStorage.getItem('preferredVideoDeviceId') }
            : undefined,
        };

        cameraStream =
          await meeting.webex.meetings.mediaHelpers.createCameraStream(videoConstraints);

        registerTracks(cameraStream, activeCameraTracks);
      } else {
        logger.info('[getMediaStreams] Camera skipped — isVideoEnabled is false');
      }

      logger.debug('Media streams created', {
        microphoneStream: !!microphoneStream,
        cameraStream: !!cameraStream,
        outputStream: !!cameraStream?.outputStream,
        outputStreamId: cameraStream?.outputStream?.id,
      });

      set({
        audioStream: microphoneStream,
        videoStream: cameraStream,
        localVideo: cameraStream?.outputStream || null,
      });

      logger.debug('State updated with local video', !!cameraStream?.outputStream);
      return { microphone: microphoneStream, camera: cameraStream };
    } catch (error: any) {
      logger.error('Error getting media streams', error);
      throw error;
    }
  },

  // userInitiated=false for internal teardown that immediately reconnects (room switch).
  leaveMeeting: async (userInitiated: boolean = true) => {
    const { meeting, webex } = get();
    // Hanging up supersedes any join still in flight — otherwise it keeps going after the
    // leave, re-raises isStartingMeeting and rejoins, leaving the connecting overlay up.
    if (userInitiated) ++_webexInitGeneration;

    // Sweep every meeting the SDK still holds, not just the one the store tracked — a
    // failed join can leave a live locus with no store reference, which would otherwise
    // keep counting toward Webex's concurrent-meeting limit.
    const meetingsToLeave: any[] = meeting ? [meeting] : [];
    const allMeetings = webex?.meetings?.getAllMeetings?.();
    if (Array.isArray(allMeetings)) {
      allMeetings.forEach((mtg: any) => {
        if (!meetingsToLeave.includes(mtg)) meetingsToLeave.push(mtg);
      });
    }

    // Stop captions silently — remaining participants keep theirs.
    await get().stopLiveCaptions(false);
    unsubscribeWebexLiveCaptions();

    try {
      // Unconditional — gating on isJitsiConnected skipped teardown for a connect still
      // in flight, which then joined the room being left and kept the user in it.
      logger.info('[NEX-817] Cleaning up Jitsi on leave...');
      await get().disconnectJitsiScreenShare();

      for (const mtg of meetingsToLeave) {
        if (mtg !== meeting) {
          logger.warn(
            '[leaveMeeting] Leaving SDK-held meeting not tracked by the store — likely a failed join left a live locus'
          );
        }
        await leaveMeetingOnce(mtg);
      }
    } catch (error: any) {
      if (isAlreadyLeftError(error)) {
        logger.debug('Meeting participant already left, nothing to leave', error?.message);
      } else {
        logger.error('Error leaving meeting:', error);
      }
    } finally {
      drainAndStop(activeCameraTracks);
      drainAndStop(activeMicrophoneTracks);

      logger.debug('❌ DESTROY MEETING: Setting isInMeeting to false', { userInitiated });
      // Hanging up abandons any reboot in flight — otherwise the overlay would outlive the
      // meeting the user just chose to leave, until the wait window lapsed.
      if (userInitiated) clearRebootWait();
      set({
        meeting: null,
        meetingInfo: null,
        isMeetingHungUp: userInitiated,
        isInMeeting: false,
        // User-initiated hangup also dismisses the connecting spinner; internal
        // (room-switch) leaves keep it — the running initializeWebex owns it then.
        ...(userInitiated
          ? {
              isStartingMeeting: false,
              isJoinPending: false,
              joinStage: '',
              isRestarting: false,
              restartReason: null,
            }
          : {}),
        isWebexRecording: false,
        webexHostReclaimed: false,
        participants: [],
        localVideo: null,
        remoteVideo: null,
        audioStream: null,
        videoStream: null,
        remoteVideos: [],
        remoteAudios: [],
        screenShareVideo: null,
        localScreenShareVideo: null,
        localScreenShareAudio: null,
        isScreenSharing: false,
        // NOTE: intentionally NOT reset — user preferences
      });
    }

    // User-initiated leave = leaving the Webex floor entirely, so dispose the SDK instance.
    // keepHungUp: cleanup would otherwise reset isMeetingHungUp to false, and CoreWebexAuth
    // reads "not hung up" as "user wants to be in the call" and auto-reconnects.
    if (userInitiated) {
      await get().cleanup(true, true);
    }
  },

  leaveMeetingExpired: async () => {
    const { meeting } = get();
    if (!meeting) return;

    // Stop captions silently — remaining participants keep theirs.
    await get().stopLiveCaptions(false);
    unsubscribeWebexLiveCaptions();

    try {
      if (get().isJitsiConnected) {
        logger.info('[NEX-817] Cleaning up Jitsi on leave...');
        await get().disconnectJitsiScreenShare();
      }
      await leaveMeetingOnce(meeting);
    } catch (error: any) {
      if (isAlreadyLeftError(error)) {
        logger.debug('Meeting participant already left, nothing to leave', error?.message);
      } else {
        logger.error('Error leaving meeting:', error);
      }
    } finally {
      drainAndStop(activeCameraTracks);
      drainAndStop(activeMicrophoneTracks);

      // An expiry supersedes any reboot in flight — otherwise the overlay would outlive
      // the expired-meeting UI until the wait window lapsed.
      clearRebootWait();

      logger.debug('❌ DESTROY MEETING: Keeping isInMeeting true to show grid');
      set({
        meeting: null,
        meetingInfo: null,
        isMeetingHungUp: true,
        isStartingMeeting: false,
        isRestarting: false,
        restartReason: null,
        isWebexRecording: false,
        webexHostReclaimed: false,
        participants: [],
        localVideo: null,
        remoteVideo: null,
        audioStream: null,
        videoStream: null,
        remoteVideos: [],
        remoteAudios: [],
        screenShareVideo: null,
        localScreenShareVideo: null,
        localScreenShareAudio: null,
        isScreenSharing: false,
        // NOTE: intentionally NOT reset — user preferences
      });
    }
  },

  /** Start Webex-native captions (Webex Assistant); user toggles broadcast to the room; true when started. */
  startLiveCaptions: async (emitBroadcast: boolean = true): Promise<boolean> => {
    const { meeting } = get();
    const { setTranscriptionStatus, setIsEnabled } = useLiveCaptionsStore.getState();

    if (!isInLiveWebexMeeting()) {
      logger.warn('[WebEx] ❌ Cannot start captions — not in a live Webex meeting/floor');
      setTranscriptionStatus('error');
      return false;
    }

    const webexNativeSupported = !!meeting?.isTranscriptionSupported?.();
    logger.info('[WebEx] Webex Assistant supported for captions:', webexNativeSupported);

    if (!webexNativeSupported) {
      logger.warn(
        '[WebEx] ❌ Webex-native captions unavailable — Webex Assistant required (paid plan)'
      );
      setTranscriptionStatus('error');
      return false;
    }

    try {
      // startTranscription() throws unless the LLM socket is up — wait for it (kick a stalled connection once).
      setTranscriptionStatus('connecting');

      setTimeout(() => {
        // Kick a stalled automatic connection — idempotent in the SDK.
        if (!isWebexLlmConnected(meeting)) {
          meeting
            ?.updateLLMConnection?.()
            .catch((error: any) => logger.warn('[WebEx] LLM connection kick failed:', error));
        }
      }, 2500);

      const llmConnected = await waitForWebexLlmConnected(meeting, 10000);
      if (!llmConnected) {
        setTranscriptionStatus('error');
        return false;
      }

      // receiveTranscription alone doesn't turn it on — explicit start.
      await meeting.startTranscription?.();
    } catch (error) {
      logger.warn('[WebEx] startTranscription failed (may already be running):', error);
    }

    setTranscriptionStatus('connected');
    setIsEnabled(true);
    set({ isLiveCaptionsActive: true });

    if (emitBroadcast) {
      emitWebexLiveCaptionsToggle(true);
    }

    logger.info('[WebEx] ✅ Webex-native live captions enabled');
    return true;
  },

  /** Stop live captions; emitBroadcast=false on leave/end so others keep theirs. */
  stopLiveCaptions: async (emitBroadcast: boolean = true) => {
    const { meeting } = get();
    if (!get().isLiveCaptionsActive) return;

    try {
      await meeting?.stopTranscription?.();
    } catch (error) {
      logger.error('[WebEx] Error stopping live captions:', error);
    } finally {
      const { setTranscriptionStatus, setIsEnabled } = useLiveCaptionsStore.getState();
      setTranscriptionStatus('idle');
      setIsEnabled(false);
      set({ isLiveCaptionsActive: false });

      if (emitBroadcast) {
        emitWebexLiveCaptionsToggle(false);
      }
    }
  },

  /**
   * Cycle the meeting so Webex finalizes a just-stopped recording.
   *
   * endMeetingForAll() is what makes this reliable: it terminates the locus server-side, so
   * every participant is ejected whether or not they received any broadcast of ours. A
   * choreographed "everybody leave" would instead depend on all clients cooperating, and a
   * single straggler keeps the locus alive — the recording then never finalizes, silently.
   *
   * Moderator-only on the Webex side. Every non-guest joins as moderator (see joinMeeting),
   * and only paid accounts can reach the recording control at all, so the caller qualifies.
   */
  restartMeetingForAll: async () => {
    const { meeting, room } = get();
    // Logged unconditionally on entry: there is more than one recording-stop button, and
    // the absence of this line is what distinguishes "the cycle failed" from "the button
    // that was clicked never starts the cycle at all".
    logger.info('[reboot] restartMeetingForAll invoked', { room, hasMeeting: !!meeting });

    if (!meeting || !room) {
      logger.warn('[reboot] restartMeetingForAll called with no active meeting');
      return;
    }

    set({ isRestarting: true, restartReason: 'reboot', isRestartInitiator: true });
    // 'preparing' rather than a reboot-specific stage: the overlay shows the same step list
    // as a normal join, so the cycle reads as connecting rather than as recording plumbing.
    get().setJoinStage('preparing');

    try {
      await meeting.endMeetingForAll();
      logger.info('[reboot] Ended meeting for all — recording will finalize', { room });
      // This client initiated the end, so its own meeting:self:left / meeting:removed —
      // should either fire — is an expected part of the cycle, not a surprise ejection.
      // Marking it left makes the ejection handling below skip (no network-drop rejoin
      // racing the cycle), and keeps the teardown deterministic here.
      meeting[LEAVE_ISSUED] = true;
      // Drop it from the SDK's collection now — the rejoin's createMeeting must not find
      // this ended object via getMeetingByType and recycle it (see leaveMeetingOnce).
      meeting.webex?.meetings?.meetingCollection?.delete?.(meeting.id);
    } catch (error) {
      // Nothing was torn down, so drop straight back to the live meeting rather than
      // stranding everyone behind the overlay.
      logger.error('[reboot] endMeetingForAll failed — aborting reboot', error);
      clearRebootWait();
      set({ isRestarting: false, restartReason: null });
      get().setJoinStage('');
      throw error;
    }

    // Run the ejection handling directly instead of waiting for this client's own
    // meeting:self:left — don't bet the cycle on an event we've observed not arriving.
    // If self:left does also fire, it is idempotent against this: same state, same timer,
    // and cleanup over already-drained tracks.
    //
    // cleanup MUST be awaited: its resetState nulls localVideo/participants/remoteVideos,
    // and fire-and-forget let it land after the rejoin had already repopulated them —
    // the ender would rejoin with a disabled local tile and no remote tiles. Awaiting it
    // here serializes the teardown before the gap, so it can never overlap the rejoin.
    clearRebootWait();
    set({
      isMeetingHungUp: true,
      isInMeeting: false,
      isReconnecting: false,
      meeting: null,
      participants: [],
      isStartingMeeting: false,
    });
    // The 1-2s gap starts NOW, in parallel with the teardown cleanup — the restart event
    // must go out 1-2 seconds after the meeting ends, not 1-2 seconds after cleanup
    // finishes (captions/Jitsi teardown can add a second on top). Cleanup still completes
    // before the emit: participants only rejoin once it has, and this client's own rejoin
    // can never race the cleanup's resetState.
    const gapDelay = new Promise<void>((resolve) => setTimeout(resolve, REBOOT_REJOIN_DELAY_MS));
    try {
      // Dispose the SDK instance: dying deltas from the ended meeting otherwise land on
      // the fresh meeting (the router falls back to correlationId/sipUri matching), desync
      // its locus parser (409 streamingSync), and freeze the recording hints pre-recording.
      // Unregistering drops the mercury connection those deltas arrive on.
      await get().cleanup(true, true);
      // cleanup() always clears joinStage — restore it so the overlay keeps showing a
      // live step instead of going blank for the rest of the cycle.
      const s = useWebexStore.getState();
      if (s.isRestarting && !s.isStartingMeeting) set({ joinStage: 'preparing' });
    } catch (error: any) {
      logger.warn('[reboot] Cleanup after reboot end failed', error);
    }
    _rebootWaitTimer = setTimeout(() => {
      _rebootWaitTimer = null;
      if (!useWebexStore.getState().isRestarting) return;
      logger.info('[reboot] No restart within the reboot window — treating as a real end');
      set({ isRestarting: false, joinStage: '', restartReason: null });
    }, REBOOT_WAIT_MS);

    // Hold the rejoin off until the locus has had time to tear down — rejoining too soon
    // risks Webex treating it as the same instance, and the recording never finalizing.
    await gapDelay;

    // The recording initiator rejoins FIRST. The restart event goes out only once our own
    // join has actually landed, so the rest of the room follows the initiator into the new
    // instance. No artificial sleep for anyone: the ordering cost is the initiator's real
    // join time — which is exactly what "initiator first" means.
    //
    // Pre-mark the cycle as processed so our own deliveries of this event (postMessage,
    // BroadcastChannel, socket echo) hit the dedupe and skip — they must not trigger a
    // second rejoin. If our rejoin failed, the manual Reconnect in the hung-up UI is the
    // recovery; the room still green-lights below so nobody else waits on us.
    const cycleId = nanoid();
    shouldProcessRestartCycle(room, cycleId, undefined);
    try {
      await get().reInitializeWebex();
      logger.info('[reboot] Initiator rejoined — green-lighting the rest of the room');
    } catch (error: any) {
      logger.warn('[reboot] Initiator rejoin failed — still green-lighting the room', error);
    }

    // Green-lights every remaining client.
    get().emitMeetingRestart(room, { reason: 'reboot', cycle_id: cycleId });
  },

  emitMeetingExpired: (roomId: string) => {
    logger.info('[WebEx] Emitting meeting expired event', { roomId });

    window.postMessage(
      {
        type: 'webex-meeting-expired',
        data: { roomId, timestamp: new Date().toISOString() },
      },
      window.location.origin
    );

    try {
      const channel = new BroadcastChannel('webex-meeting-channel');
      channel.postMessage({
        type: 'meeting-expired',
        roomId,
        timestamp: new Date().toISOString(),
      });
      channel.close();
    } catch (error) {
      logger.warn('[WebEx] BroadcastChannel not supported', error);
    }
  },

  emitMeetingRestart: (roomId: string, meta?: { reason?: string; cycle_id?: string }) => {
    const { reason } = meta ?? {};
    // Every emit gets a unique id: one emit reaches a client three ways (direct
    // postMessage, BroadcastChannel, and the socket topic echo), and receivers process each
    // cycle exactly once keyed on this.
    const cycle_id = meta?.cycle_id ?? nanoid();
    const timestamp = new Date().toISOString();
    logger.info('[WebEx] 📤 Emitting meeting restart event', { roomId, reason, cycle_id });
    console.log('[WebEx] 📤 EMIT RESTART - Room ID:', roomId);

    window.postMessage(
      {
        type: 'webex-meeting-restart',
        data: { roomId, timestamp, cycleId: cycle_id, ...(reason ? { reason } : {}) },
      },
      window.location.origin
    );

    try {
      const channel = new BroadcastChannel('webex-meeting-channel');
      channel.postMessage({
        type: 'meeting-restart',
        roomId,
        timestamp,
        cycleId: cycle_id,
        ...(reason ? { reason } : {}),
      });
      channel.close();
    } catch (error) {
      logger.warn('[WebEx] BroadcastChannel not supported', error);
    }

    try {
      const payload = {
        room_pid: roomId,
        timestamp,
        cycle_id,
        ...(reason ? { reason } : {}),
      };
      console.log(
        '[WebEx] 🚀 Emitting socket event vo.webex.meeting.restart with payload:',
        payload
      );
      GlobalEventService.emitSafe('vo.webex.meeting.restart', payload, true);
      logger.info('[WebEx] ✅ Emitted meeting restart event via socket', { roomId });
    } catch (error) {
      logger.error('[WebEx] ❌ Error emitting meeting restart event via socket', error);
      console.error('[WebEx] Socket emit error:', error);
    }
  },

  sendChatMessage: async (text: string) => {
    const { webex, meeting, roomId } = get();
    if (!meeting || !text.trim()) return;
    if (!webex || !text.trim()) return;

    try {
      await webex.messages.create({ roomId, text: text.trim() });
      const message = {
        id: Date.now().toString(),
        text,
        sender: 'You',
        timestamp: new Date().toLocaleTimeString(),
      };
      get().addChatMessage(message);
    } catch (error: any) {
      logger.error('Error sending message:', error);
      throw error;
    }
  },

  // disposeWebex=false keeps the SDK instance alive for a fast room-switch rejoin;
  // default (true) fully disposes it for hangup / logout / auth loss.
  // keepHungUp=true skips resetting isMeetingHungUp to false — used by the join-failure
  // path, which needs it to land on (and stay) true with no gap. Setting it after cleanup
  // resolves would still race: cleanup's own resetState clears it to false as its last
  // step, and a re-render in that window would re-trigger CoreWebexAuth's auto-connect
  // effect before the caller's own reassert runs.
  cleanup: async (disposeWebex: boolean = true, keepHungUp: boolean = false) => {
    // Disable mic tracking immediately so any concurrent getUserMedia calls
    // (e.g. Jitsi rejoining a room) are not captured and stopped by this cleanup.
    _webexMicTrackingActive = false;
    const state = get();

    // Stop live captions before draining tracks (no broadcast — app teardown).
    await get().stopLiveCaptions(false);
    unsubscribeWebexLiveCaptions();

    try {
      drainAndStop(activeCameraTracks);
      drainAndStop(activeMicrophoneTracks);
      ownedMicrophoneTrackIds.clear();

      const mediaSources = [
        state.localVideo,
        state.remoteVideo,
        state.screenShareVideo,
        state.localScreenShareVideo,
        state.audioStream,
        state.videoStream,
        ...state.remoteVideos.map((rv: any) => rv.stream),
        ...state.remoteAudios.map((ra: any) => ra.stream),
      ].filter(Boolean);

      const stopMediaTracks = (source: any) => {
        try {
          if (typeof source.getTracks === 'function') {
            source.getTracks().forEach((track: MediaStreamTrack) => track.stop());
          }
        } catch (error) {
          logger.debug('Error stopping media tracks', error);
        }
      };

      mediaSources.forEach(stopMediaTracks);

      if (state.isJitsiConnected && state.jitsiService) {
        logger.info('[NEX-817] Cleaning up Jitsi connection...');
        await get().disconnectJitsiScreenShare();
      }

      // NOTE: the BroadcastChannel is a module-level singleton — closing it here
      // would kill cross-tab signalling after the first room switch.

      if (state.meeting) {
        await leaveMeetingOnce(state.meeting).catch((error: any) => {
          if (isAlreadyLeftError(error)) {
            logger.debug('Meeting participant already left during cleanup', error?.message);
          } else {
            logger.warn('Meeting leave failed', error);
          }
        });
      }

      if (state.webex) {
        const allMeetings = state.webex.meetings.getAllMeetings();
        if (Array.isArray(allMeetings)) {
          // The sweep skips meetings already left (duplicate-leave guard).
          await Promise.allSettled(allMeetings.map((mtg: any) => leaveMeetingOnce(mtg)));
        }
        if (disposeWebex) {
          await state.webex.meetings
            .unregister()
            .catch((error: any) => logger.warn('Webex unregister failed', error));
        }
      }
    } catch (error) {
      logger.error('Cleanup failed', error);
    }

    const resetState: Partial<WebexStore> = {
      meeting: null,
      meetingInfo: null,
      joinStage: '',
      isWebexRecording: false,
      webexHostReclaimed: false,
      participants: [],
      chatMessages: [],
      localVideo: null,
      remoteVideo: null,
      // isVideoEnabled / isAudioEnabled intentionally NOT reset — user preferences.
      audioStream: null,
      videoStream: null,
      screenShareVideo: null,
      localScreenShareVideo: null,
      localScreenShareAudio: null,
      remoteVideos: [],
      remoteAudios: [],
      remoteScreenShares: [],
      isScreenSharing: false,
      canMinimizeMeetingPopup: false,
      dialogPosition: { x: 0, y: 108 },
      isReconnecting: false,
      ...(keepHungUp ? {} : { isMeetingHungUp: false }),
      jitsiService: null,
      jitsiSubscription: null,
      isJitsiConnected: false,
      jitsiScreenShareCount: 0,
      // broadcastChannel intentionally NOT reset — see note above.
    };

    if (disposeWebex) {
      set({ ...resetState, webex: null, isConnected: false, isInMeeting: false });
    } else {
      // Keep the SDK instance + connection for the next room's join.
      set(resetState);
    }
  },
}));

/**
 * Display name of the participant who started the current Webex recording, resolved from
 * the SDK's `modifiedBy` member id against the participant list. Mirrors the Jitsi bar's
 * "Recording By {name}" (PanelRecordingBlock). Null when the initiator is unknown — an
 * older recording, or a member who has since left — so callers can fall back to a generic
 * label rather than rendering an id.
 */
export const selectWebexRecordingInitiatorName = (state: any): string | null => {
  const modifiedBy = state?.webexRecordingModifiedBy;
  if (!modifiedBy) return null;

  // modifiedBy is a SELF-IDENTITY (Webex person id). The store's participants array holds
  // raw members:update delta payloads, which don't carry selfIdentity/person — but the
  // SDK's member INSTANCES in meeting.members.membersCollection do (selfIdentity getter =
  // person.id, the exact namespace modifiedBy uses). Look there first.
  const collection = state?.meeting?.members?.membersCollection;
  if (collection && typeof collection.values === 'function') {
    for (const member of collection.values()) {
      if (member?.selfIdentity === modifiedBy && member?.name) {
        return member.name;
      }
    }
  }

  // Fallback: raw participant entries (some builds/events may include the fields).
  const member = state?.participants?.find(
    (p: any) => p.selfIdentity === modifiedBy || p.person?.id === modifiedBy || p.id === modifiedBy
  );
  return member?.name ?? null;
};

/**
 * Is the local user the one who last started/stopped the Webex recording? Fails open when
 * either identity is unknown. Shared by WebexRecordingControl and ToggleRecording.
 */
export const isWebexRecordingInitiator = (
  meeting: any,
  modifiedBy: string | null | undefined,
  participants: any[]
): boolean => {
  if (!modifiedBy) return true;

  // modifiedBy is a SELF-IDENTITY (Webex person id) — the SDK compares it as
  // selfIdentity !== modifiedBy, so match it against the same identity fields, with
  // member/participant ids as a secondary net for builds that report those instead.
  const selfIdentities = [
    meeting?.locusInfo?.selfIdentity,
    meeting?.locusInfo?.parsedLocus?.self?.selfIdentity,
    meeting?.locusInfo?.parsedLocus?.self?.person?.id,
  ].filter(Boolean) as string[];

  const selfMemberIds = [
    meeting?.members?.selfId,
    meeting?.locusInfo?.selfId,
    meeting?.locusInfo?.parsedLocus?.self?.id,
    meeting?.locusInfo?.parsedLocus?.self?.participantId,
    meeting?.locusInfo?.parsedLocus?.self?.selfId,
    participants.find((p: any) => p.isSelf)?.id,
  ].filter(Boolean) as string[];

  if (selfIdentities.length === 0 && selfMemberIds.length === 0) return true;
  return selfIdentities.includes(modifiedBy) || selfMemberIds.includes(modifiedBy);
};

/** SDK's RecordingController rejecting on its own stale displayHints copy — not our role. */
export const isRecordingHintPermissionError = (error: any): boolean =>
  String(error?.message ?? '').includes('due to moderator property');

/** Re-push the locus hints into the recording controller; a missed delta leaves it stale. */
const refreshRecordingHints = (meeting: any): void => {
  try {
    let hints = meeting?.locusInfo?.parsedLocus?.info?.userDisplayHints;
    // Reclaim accepted server-side but the locus copy may never learn it — inject the
    // recording controls so the SDK's own pre-check passes.
    if (
      useWebexStore.getState().webexHostReclaimed &&
      Array.isArray(hints) &&
      !hints.includes('RECORDING_CONTROL_START')
    ) {
      hints = [
        ...hints,
        'RECORDING_CONTROL_START',
        'RECORDING_CONTROL_STOP',
        'RECORDING_CONTROL_PAUSE',
        'RECORDING_CONTROL_RESUME',
      ];
      logger.warn(
        '[recording] Injected recording hints for reclaimed host — locus hints were stale'
      );
    }
    if (Array.isArray(hints)) meeting?.recordingController?.setDisplayHints?.(hints);
    meeting?.recordingController?.setUserPolicy?.(meeting?.selfUserPolicies);
    // The button gates on inMeetingActions, which derives from these hints.
    meeting?.updateMeetingActions?.();
  } catch (error) {
    logger.warn('[recording] Display hint refresh failed', error);
  }
};

/**
 * The facade checks its own displayHints copy, which lags the one driving the button.
 * Escalate: re-push the hints we hold, then resync loci from the server, then give up.
 */
export const webexRecordingToggleWithHintLagRetry = async (
  meeting: any,
  action: 'start' | 'stop'
): Promise<void> => {
  const invoke = () => (action === 'start' ? meeting.startRecording() : meeting.stopRecording());
  try {
    await invoke();
    return;
  } catch (error: any) {
    if (!isRecordingHintPermissionError(error)) throw error;
    logger.warn(`[recording] ${action} rejected by stale display hints — refreshing and retrying`);
  }

  refreshRecordingHints(meeting);
  await new Promise((resolve) => setTimeout(resolve, 800));
  try {
    await invoke();
    return;
  } catch (error: any) {
    if (!isRecordingHintPermissionError(error)) throw error;
    logger.warn(`[recording] ${action} still rejected — resyncing loci from the server`);
  }

  try {
    await meeting?.webex?.meetings?.syncMeetings?.({ keepOnlyLocusMeetings: false });
  } catch (error) {
    logger.warn('[recording] Locus resync before the final retry failed', error);
  }
  refreshRecordingHints(meeting);
  await new Promise((resolve) => setTimeout(resolve, 500));
  await invoke();
};

/** Fold a slot-keyed placeholder tile into the real member once the roster makes it unambiguous. */
const reconcilePendingSlotTiles = (get: any, set: any): void => {
  const { participants, remoteVideos } = get();
  const pending = participants.filter((p: any) => p.isPendingSlot);
  if (!pending.length) return;

  const boundIds = new Set(
    remoteVideos.filter((v: any) => v.stream).map((v: any) => v.participantId)
  );
  const unattributed = participants.filter(
    (p: any) => !p.isSelf && !p.isPendingSlot && !boundIds.has(p.id)
  );
  if (pending.length !== 1 || unattributed.length !== 1) return;

  const placeholder = pending[0];
  const member = unattributed[0];
  logger.debug('[WC] folding pending slot tile into member', {
    slotKey: placeholder.id,
    memberId: member.id,
  });
  set({
    participants: participants.filter((p: any) => p.id !== placeholder.id),
    remoteVideos: remoteVideos.map((v: any) =>
      v.participantId === placeholder.id ? { ...v, participantId: member.id } : v
    ),
  });
};

// Meeting event listeners
const setupMeetingEventListeners = (meeting: any, { set, get }: any) => {
  meeting.on('error', (err: any) => {
    logger.error('Meeting error:', err);
  });

  // Recording state, authoritative from the SDK. meeting.recording is populated from the
  // meeting's locus controls, which arrive on join — so this is correct after a refresh or
  // remount, where the store flag (only ever set by our own click handlers) was stale.
  // The event names carry the `meeting:` prefix and the state values are lowercase; both
  // were wrong before, so this sync had never actually run.
  try {
    const syncRecordingState = () => {
      // The pre-reboot meeting object keeps emitting after the rejoin — a late
      // recording:stopped would overwrite the new session's status and initiator.
      if (get().meeting !== meeting) return;
      const recording = meeting?.recording;
      const state = recording?.state;
      const isRecording = RECORDING_STATES.includes(state);
      if (get().isWebexRecording !== isRecording) {
        logger.info('[recording] SDK recording state', { state, isRecording });
      }
      set({
        isWebexRecording: isRecording,
        // Who started/stopped it — lets a client tell "I started this" from "someone else did".
        webexRecordingModifiedBy: recording?.modifiedBy ?? null,
      });
    };
    syncRecordingState();

    meeting.on('meeting:recording:started', syncRecordingState);
    meeting.on('meeting:recording:stopped', syncRecordingState);
    meeting.on('meeting:recording:paused', syncRecordingState);
    meeting.on('meeting:recording:resumed', syncRecordingState);
  } catch (error) {
    logger.debug('[setupMeetingEventListeners] SDK recording events unavailable', error);
  }

  // Refine the join-progress stage from the SDK's own state machine/media events.
  try {
    meeting.on('meeting:stateChange', (payload: any) => {
      const state = payload?.currentState ?? payload?.state;
      if (typeof state !== 'string') return;
      if (!get().isStartingMeeting) return;
      if (state === 'RINGING' || state === 'JOINING') {
        get().setJoinStage('joining');
      } else if (state === 'JOINED') {
        get().setJoinStage('media');
      }
    });
    meeting.on('media:ready', () => {
      if (get().isStartingMeeting) get().setJoinStage('media');
    });
  } catch (error) {
    logger.debug('[setupMeetingEventListeners] SDK state/media events unavailable', error);
  }

  // Surface connection hiccups so the UI can distinguish "reconnecting" from "meeting ended" —
  // the countdown keeps ticking (it's wall-clock math, not tied to connection state).
  try {
    meeting.on('meeting:reconnectionStarting', () => {
      if (get().meeting !== meeting) return;
      set({ isReconnecting: true });
    });
    meeting.on('meeting:reconnectionSuccess', () => {
      if (get().meeting !== meeting) return;
      set({ isReconnecting: false });
    });
    meeting.on('meeting:reconnectionFailure', () => {
      if (get().meeting !== meeting) return;
      set({ isReconnecting: false });
    });
  } catch (error) {
    logger.debug('[setupMeetingEventListeners] SDK reconnection events unavailable', error);
  }

  meeting.members.on('members:update', (payload: any) => {
    // The pre-reboot meeting object keeps emitting after the rejoin and mutates the
    // shared store — a late NOT_IN_MEETING for a member the NEW session already re-added
    // would delete them from the grid. Only the meeting the store tracks may write state.
    if (get().meeting !== meeting) {
      logger.debug('[WC] members:update ignored — stale meeting object', {
        meetingId: meeting.id,
      });
      return;
    }

    const delta = payload.delta;
    logger.debug('[WC] members:update', {
      meetingId: meeting.id,
      added: (delta.added && Object.keys(delta.added).length) || 0,
      updated: (delta.updated && Object.keys(delta.updated).length) || 0,
    });

    if (delta.added && delta.added.length > 0) {
      Object.keys(delta.added).forEach((key) => {
        const member = delta.added[key];
        logger.debug('[WC] member added', {
          id: member.id,
          name: member.name,
          isInMeeting: member.isInMeeting,
          isInLobby: member.isInLobby,
          status: member.status,
        });
        if (member && member.isInMeeting) {
          const { participants } = get();
          const existingIndex = participants.findIndex((p: any) => p.id === member.id);
          if (existingIndex === -1) {
            set({ participants: [...participants, member] });
            logger.info('Added participant', { id: member.id, name: member.name });
          } else {
            // A stream placeholder may be sitting under this id (created before the member
            // data arrived) — replace it so the card shows the real name, not "Participant".
            const existing = participants[existingIndex];
            if ((existing as any)?.name?.startsWith?.('Participant')) {
              const next = [...participants];
              next[existingIndex] = member;
              set({ participants: next });
              logger.debug('[WC] replaced stream placeholder with real member:', member.id);
            } else {
              logger.debug('Participant already exists, skipping:', member.id);
            }
          }
        }
        if (member && member.isInLobby) {
          const { room, token } = get();
          if (room && token) {
            try {
              get()
                .admitEveryoneInLobby(room, token)
                .then(() => {
                  const { participants } = get();
                  if (!participants.some((p: any) => p.id === member.id)) {
                    set({ participants: [...participants, member] });
                  }
                })
                .catch((error: any) => {
                  // The member's IN_LOBBY → IN_MEETING transition is inserted by the
                  // delta.updated handler below, so a failed sweep is not fatal — but the
                  // rejection must not go unhandled.
                  logger.warn(
                    '[members] admitEveryoneInLobby failed for added lobby member',
                    error
                  );
                });
            } catch (error) {
              logger.error('Error admitting participants:', error);
            }
          }
        }
      });
    }

    if (delta.updated) {
      Object.values(delta.updated).forEach((member: any) => {
        logger.debug('MemberUpdate', member);
        if (member.status === 'IN_LOBBY' && member.id) {
          const { room, token } = get();
          // Without room/token this built a malformed admit URL — skip quietly.
          if (!room || !token) return;

          const now = Date.now();
          const lastAdmit = _lastLobbyAdmitAt.get(member.id) ?? 0;
          if (now - lastAdmit < LOBBY_ADMIT_THROTTLE_MS) return;
          _lastLobbyAdmitAt.set(member.id, now);

          // Opportunistic prune so the map cannot grow across a long session.
          if (_lastLobbyAdmitAt.size > 200) {
            for (const [admitKey, admittedAt] of _lastLobbyAdmitAt) {
              if (now - admittedAt >= LOBBY_ADMIT_THROTTLE_MS) {
                _lastLobbyAdmitAt.delete(admitKey);
              }
            }
          }

          get().admitToRoom(room, token, [member.id]);
        }
        // A member who was IN_LOBBY at add time is skipped by the delta.added branch
        // above (isInMeeting false). Their lobby→meeting transition arrives here, and the
        // update branches only replace existing entries — so without this insert, a guest
        // admitted after joining never appears on other clients' grids. This is the whole
        // "host can't see the guest after a reboot" bug.
        if ((member.status === 'IN_MEETING' || member.isInMeeting === true) && member.id) {
          const { participants: current } = get();
          const exists = current.some((p: any) => p.id === member.id);
          if (!exists) {
            set({ participants: [...current, member] });
            logger.info('[members] Added participant after lobby admission', {
              id: member.id,
              name: member.name,
            });
          }
        }
        // No .length guard — delta.updated may be a plain object, not an array.
        if (member.status === 'NOT_IN_MEETING' && member.id) {
          logger.debug('🚨 [first-pass] NOT_IN_MEETING - removing participant:', member.id);
          const { participants: currentParticipants, remoteVideos: currentRemoteVideos } = get();
          set({
            participants: currentParticipants.filter((p: any) => p.id !== member.id),
            remoteVideos: currentRemoteVideos.filter((v: any) => v.participantId !== member.id),
          });
        }
      });
    }

    if (delta.updated && delta.updated.length > 0) {
      // Latest roster — the first updated pass above may have inserted admitted members.
      const updatedParticipants = [...get().participants];
      const removedMemberIds: string[] = [];
      Object.keys(delta.updated).forEach((key) => {
        const member = delta.updated[key];
        logger.debug('MemberUpdate', member);
        const index = updatedParticipants.findIndex((p: any) => p.id === member.id);
        if (index > -1) {
          if (member.status === 'NOT_IN_MEETING') {
            logger.debug('🚨 PARTICIPANT STATUS NOT_IN_MEETING - REMOVING:', member.name);
            updatedParticipants.splice(index, 1);
            if (member.id) removedMemberIds.push(member.id);
          } else {
            updatedParticipants[index] = member;
          }
        }
      });
      const { remoteVideos } = get();
      const updatedRemoteVideos =
        removedMemberIds.length > 0
          ? remoteVideos.filter((v: any) => !removedMemberIds.includes(v.participantId))
          : remoteVideos;
      set({ participants: updatedParticipants, remoteVideos: updatedRemoteVideos });
    }

    if (delta.removed && delta.removed.length > 0) {
      const { participants: latestParticipants } = get();
      logger.info('Participants removed from meeting', {
        removed: delta.removed,
        beforeRemoval: latestParticipants.map((p: any) => ({ id: p.id, name: p.name })),
      });
      const removedIds = delta.removed.map((r: any) => r.id);
      const updatedParticipants = latestParticipants.filter((p: any) => !removedIds.includes(p.id));
      logger.debug(
        'Participants after removal',
        updatedParticipants.map((p: any) => ({ id: p.id, name: p.name }))
      );
      const { remoteVideos } = get();
      const updatedRemoteVideos = remoteVideos.filter(
        (v: any) => !removedIds.includes(v.participantId)
      );
      set({ participants: updatedParticipants, remoteVideos: updatedRemoteVideos });
    }

    // Roster changed — a slot-parked stream may now be attributable. See sourceUpdate.
    reconcilePendingSlotTiles(get, set);
  });

  meeting.on('media:remoteAudio:created', (audioMediaGroup: any) => {
    if (get().meeting !== meeting) return;
    logger.debug('[WC] remoteAudio created', {
      meetingId: meeting.id,
      count: audioMediaGroup?.getRemoteMedia?.().length ?? 0,
    });
    audioMediaGroup.getRemoteMedia().forEach((media: any) => {
      const { remoteAudios } = get();
      const newRemoteAudios = [...remoteAudios];
      newRemoteAudios.push({
        stream: media.stream,
        participantId: media.participantId || media.memberId || 'unknown',
      });
      get().setRemoteAudios(newRemoteAudios);
    });
  });

  meeting.on('meeting:stoppedSharingLocal', () => {
    logger.info('Local screen share stopped');
    markShareLayoutChange();
    const { localScreenShareVideo, localScreenShareAudio } = get();

    if (localScreenShareVideo) {
      const actualVideoStream =
        (localScreenShareVideo as any).outputStream || localScreenShareVideo;
      actualVideoStream.getTracks?.().forEach((track: MediaStreamTrack) => {
        track.stop();
        logger.debug('Stopped screen share video track', { trackId: track.id });
      });
    }
    if (localScreenShareAudio) {
      const actualAudioStream =
        (localScreenShareAudio as any).outputStream || localScreenShareAudio;
      actualAudioStream.getTracks?.().forEach((track: MediaStreamTrack) => {
        track.stop();
        logger.debug('Stopped screen share audio track', { trackId: track.id });
      });
    }

    set({ isScreenSharing: false, localScreenShareVideo: null, localScreenShareAudio: null });
  });

  meeting.on('meeting:startedSharingRemote', async () => {
    markShareLayoutChange();
    try {
      await meeting.remoteMediaManager?.setLayout('ScreenShareView');
    } catch {
      // Ignore layout errors
    }

    const { isJitsiConnected } = get();
    if (!isJitsiConnected) {
      try {
        logger.info('[Webex] 🔌 Remote screen share detected, connecting to Jitsi...');
        await get().connectJitsiForScreenShare();
        logger.info('[Webex] ✅ Connected to Jitsi, receiving screen shares');
      } catch (jitsiError) {
        logger.warn('[Webex] ⚠️ Failed to connect to Jitsi:', jitsiError);
      }
    }
  });

  meeting.on('media:remoteVideo:layoutChanged', (event: any) => {
    markShareLayoutChange();
    logger.debug('[WC] layoutChanged', {
      meetingId: meeting.id,
      hasMainPane: !!event.activeSpeakerVideoPanes?.main,
      hasThumbnailPane: !!event.activeSpeakerVideoPanes?.thumbnails,
      hasScreenSharePanes: !!event.screenShareVideoPanes,
    });
    logger.debug('[WebexStore:layoutChanged]', {
      hasScreenShareVideo: !!event.screenShareVideo,
      hasScreenShareVideoPanes: !!event.screenShareVideoPanes,
      hasActiveSpeakerVideoPanes: !!event.activeSpeakerVideoPanes,
      eventKeys: Object.keys(event),
    });

    const { screenShareVideo, activeSpeakerVideoPanes, screenShareVideoPanes } = event;
    const currentScreenShares: { stream: MediaStream; participantId: string }[] = [];

    if (screenShareVideoPanes?.getRemoteMedia) {
      try {
        const remoteMedias = screenShareVideoPanes.getRemoteMedia();
        logger.debug('[WebexStore] screenShareVideoPanes media count:', remoteMedias.length);
        remoteMedias.forEach((remoteMedia: any, index: number) => {
          if (remoteMedia.stream) {
            const participantId =
              remoteMedia.participantId || remoteMedia.memberId || `ss-${index}`;
            currentScreenShares.push({ stream: remoteMedia.stream, participantId });
            logger.debug('[WebexStore] Added screen share:', participantId);
          }
        });
      } catch (error) {
        logger.error('[WebexStore] Error reading screenShareVideoPanes:', error);
      }
    }

    if (currentScreenShares.length === 0 && screenShareVideo?.stream) {
      const currentState = get();
      const hasActiveLocalShare =
        currentState.localScreenShareVideo && currentState.isScreenSharing;

      logger.debug('Screen share stream detected', {
        hasActiveLocalShare,
        remoteStreamId: screenShareVideo.stream.id,
        localStreamId: hasActiveLocalShare
          ? (currentState.localScreenShareVideo as any)?.id
          : 'none',
      });

      if (!hasActiveLocalShare) {
        logger.debug('No local share detected, waiting for sourceUpdate confirmation');
        set({
          screenShareVideo: screenShareVideo.stream,
          localScreenShareVideo: null,
          localScreenShareAudio: null,
        });

        if (screenShareVideo.on) {
          screenShareVideo.on('sourceUpdate', (d: any) => {
            const isLive = d?.state === 'live';
            logger.debug(`Remote screen share sourceUpdate: ${d?.state}`);
            if (isLive) {
              logger.info('Remote screen share confirmed live');
              set({
                screenShareVideo: screenShareVideo.stream,
                isScreenSharing: true,
                localScreenShareVideo: null,
                localScreenShareAudio: null,
              });
            } else {
              logger.debug('Remote screen share ended');
              set({ screenShareVideo: null, isScreenSharing: false });
            }
          });
        }
      } else {
        logger.debug('Ignoring remote screen share - active local share detected');
      }
    } else if (!get().localScreenShareVideo) {
      set({ screenShareVideo: null, isScreenSharing: false });
    } else {
      set({ screenShareVideo: null });
    }

    if (currentScreenShares.length > 0) {
      logger.debug('[WebexStore] Setting remoteScreenShares:', currentScreenShares.length);
      set({ remoteScreenShares: currentScreenShares });
    }

    const remoteVideoPane = activeSpeakerVideoPanes.main || activeSpeakerVideoPanes.thumbnails;

    if (remoteVideoPane) {
      const paneMedia = remoteVideoPane.getRemoteMedia();
      logger.debug('[WC] attaching sourceUpdate listeners', {
        meetingId: meeting.id,
        remoteMediaCount: paneMedia.length,
        alreadyAttached: paneMedia.filter((m: any) => _sourceUpdateAttached.has(m)).length,
      });
      paneMedia.forEach((remoteMedia: any, index: number) => {
        // Attach at most once per media object — see _sourceUpdateAttached.
        if (_sourceUpdateAttached.has(remoteMedia)) return;
        _sourceUpdateAttached.add(remoteMedia);

        remoteMedia.on('sourceUpdate', (data: any) => {
          // Guarded like members:update above: the old session's pane keeps emitting
          // sourceUpdate after the reboot, and a late 'no source' would remove the
          // participant the new session just added (same memberId across sessions).
          if (get().meeting !== meeting) {
            logger.debug('[WC] sourceUpdate ignored — stale meeting object', {
              state: data?.state,
              memberId: data?.memberId,
            });
            return;
          }
          // The SDK sometimes omits memberId on sourceUpdate — observed post-reboot on the
          // thumbnail pane, where the 'live' event carried only the pane index. Fall back
          // to the remoteMedia object's own identity so the video is keyed to the right
          // participant instead of landing in remoteVideos with participantId: undefined
          // (which the grid could never match — the guest's tile then renders without
          // video even though their member entry exists).
          const memberId =
            data.memberId ?? remoteMedia.memberId ?? remoteMedia.participantId ?? null;

          logger.debug('[WC] sourceUpdate', {
            state: data?.state,
            memberId,
            rawMemberId: data?.memberId,
            index,
          });

          const streamid = index.toString();
          // Stable per-slot key, relinked to the real member once the lazy memberId resolves.
          const slotKey = slotKeyForMedia(remoteMedia);

          if (data.state === 'live') {
            const { remoteVideos, participants } = get();

            // Attribute the stream instead of inventing a tile: the grid renders one tile
            // per participant, so a slot placeholder for an already-rostered member is a
            // second tile for the same person. One video-less member = unambiguous guess;
            // anything less certain parks the stream under the slot with no participant
            // (a brief avatar beats a brief duplicate).
            const boundIds = new Set(
              remoteVideos
                .filter((v: any) => v.slotKey !== slotKey && v.stream)
                .map((v: any) => v.participantId)
            );
            const unattributedMembers = participants.filter(
              (p: any) => !p.isSelf && !p.isPendingSlot && !boundIds.has(p.id)
            );
            const resolvedId =
              memberId ?? (unattributedMembers.length === 1 ? unattributedMembers[0].id : null);
            const targetId = resolvedId ?? slotKey;

            if (!memberId) {
              logger.debug('[WC] live source without memberId', {
                slotKey,
                guessedMember: resolvedId,
                candidates: unattributedMembers.length,
              });
            }

            // Upsert on slotKey — it survives the id-less → id-known transition.
            const newRemoteVideos = remoteVideos.filter(
              (v: any) => v.slotKey !== slotKey && v.participantId !== slotKey
            );
            const existingIndex = newRemoteVideos.findIndex(
              (v: any) => v.participantId === targetId
            );
            const linkedVideo = {
              stream: remoteMedia.stream,
              participantId: targetId,
              index: streamid,
              slotKey,
            };
            if (existingIndex !== -1) {
              newRemoteVideos[existingIndex] = {
                ...newRemoteVideos[existingIndex],
                ...linkedVideo,
              };
            } else {
              newRemoteVideos.push(linkedVideo);
            }
            get().setRemoteVideos(newRemoteVideos);

            // Drop the placeholder from an earlier id-less update for this same slot.
            const nextParticipants = participants.filter((p: any) => p.id !== slotKey);
            const droppedPlaceholder = nextParticipants.length !== participants.length;

            // Invent a participant only when there is genuinely no tile for this stream.
            const needsPlaceholder =
              !nextParticipants.some((p: any) => p.id === targetId) &&
              (resolvedId !== null || unattributedMembers.length === 0);
            if (needsPlaceholder) {
              logger.debug('[WC] creating placeholder participant for stream', { targetId });
              nextParticipants.push({
                id: targetId,
                name: resolvedId ? `Participant ${resolvedId}` : 'Participant',
                isInMeeting: true,
                isSelf: false,
                status: 'IN_MEETING',
                // Stand-in tile for an unidentified slot — never a guess target.
                ...(resolvedId ? {} : { isPendingSlot: true }),
              });
            }
            if (droppedPlaceholder || needsPlaceholder) set({ participants: nextParticipants });
          }

          if (data.state === 'no source') {
            logger.debug('[Webex] 🚨 PARTICIPANT LOST VIDEO SOURCE:', memberId ?? slotKey);
            const { remoteVideos, participants, setRemoteVideos } = get();

            // Track ended = abrupt disconnect — remove now instead of waiting for members:update.
            // Match slotKey too: a guessed binding is no longer findable under the slot key.
            const videoEntry = remoteVideos.find(
              (v: any) =>
                v.slotKey === slotKey || v.participantId === memberId || v.participantId === slotKey
            );
            const tracks: MediaStreamTrack[] = videoEntry?.stream?.getTracks() ?? [];
            const isActuallyDisconnected =
              tracks.length > 0 &&
              tracks.every((t) => t.readyState === 'ended') &&
              !isWebexShareTransition();

            if (isActuallyDisconnected) {
              // Whoever the stream ended up bound to is the participant that leaves.
              const boundId = videoEntry?.participantId ?? memberId ?? slotKey;
              logger.debug(
                '[Webex] 🚨 Track readyState=ended — participant disconnected, removing:',
                boundId
              );
              set({
                remoteVideos: remoteVideos.filter(
                  (v: any) => v.participantId !== boundId && v.slotKey !== slotKey
                ),
                participants: participants.filter((p: any) => p.id !== boundId),
              });
            } else {
              setRemoteVideos(
                remoteVideos.map((video: any) =>
                  video.slotKey === slotKey ? { ...video, stream: null } : video
                )
              );
            }
          }
          if (data.state === 'avatar') {
            logger.debug('🚨 PARTICIPANT SHOWING AVATAR:', memberId);
            const { remoteVideos, setRemoteVideos } = get();
            // Slot-key only: the captured pane index can now hold another participant.
            setRemoteVideos(
              remoteVideos.map((video: any) =>
                video.slotKey === slotKey ? { ...video, stream: null } : video
              )
            );
          }
          if (data.state === 'bandwidth disabled') {
            const { remoteVideos, setRemoteVideos } = get();
            setRemoteVideos(
              remoteVideos.map((video: any) =>
                video.slotKey === slotKey ? { ...video, stream: null } : video
              )
            );
          }
        });
      });
    }
  });

  meeting.on('media:remoteScreenShareAudio:created', (screenShareAudioMediaGroup: any) => {
    logger.debug(
      'remoteMedia:screenShareAudioCreated',
      screenShareAudioMediaGroup.getRemoteMedia()
    );
  });

  meeting.on('members:speaking', (event: any) => {
    const speakingMembers = event?.speakingMembers || [];
    if (speakingMembers.length > 0) {
      set({ activeSpeakerId: speakingMembers[0].id });
    } else {
      set({ activeSpeakerId: null });
    }
  });

  // Webex-native captions (Webex Assistant, paid) — fires when the LLM socket is up; startTranscription needs it.
  meeting.on('meeting:transcription:connected', () => {
    logger.info('[WebEx] Webex Assistant transcription socket connected (LLM)');
    notifyWebexLlmConnected();
  });

  meeting.on('meeting:receiveTranscription:started', (payload: any) => {
    logger.info('[WebEx] Webex Assistant transcription started', {
      spokenLanguages: payload?.spokenLanguages,
      captionLanguages: payload?.captionLanguages,
    });
  });

  meeting.on('meeting:caption-received', (payload: any) => {
    handleWebexNativeCaption(payload);
  });

  // Already left server-side — mark the meeting so teardown won't PUT /leave again.
  meeting.on('meeting:self:left', (payload: any) => {
    // A late self:left from a pre-reboot meeting object must not tear down the state of
    // the meeting the store now tracks. Skipped cases are already handled: the ender runs
    // its own deterministic teardown before this fires, and a normal leave's finally block
    // resets state itself.
    if (get().meeting !== meeting) return;

    // leaveMeetingOnce sets this *before* awaiting leave(), so a flag that is still false
    // here means the locus dropped us — an endMeetingForAll cycle, a host removal, or a
    // network inactivity drop — rather than us hanging up. That distinction is the whole
    // basis of the ejection handling, and it needs reading before the flag is overwritten.
    const wasEjected = !meeting[LEAVE_ISSUED];
    meeting[LEAVE_ISSUED] = true;
    handleEjectedFromMeeting(
      meeting,
      String(payload?.reason ?? payload?.reasonCode ?? ''),
      wasEjected
    );
  });

  // Removed participants (guests included) never get meeting:self:left — the SDK destroys
  // the meeting object and emits meeting:removed on the collection instead. Route that
  // into the same ejection handling. The meetings plugin outlives meeting objects, so the
  // hook is attached at most once per plugin.
  const meetingsPlugin = meeting?.webex?.meetings;
  if (
    meetingsPlugin &&
    typeof meetingsPlugin.on === 'function' &&
    !_ejectionHookAttached.has(meetingsPlugin)
  ) {
    _ejectionHookAttached.add(meetingsPlugin);
    meetingsPlugin.on('meeting:removed', (payload: any) => {
      const state = useWebexStore.getState();
      // Only the meeting the store still tracks matters — stale removals for meetings we
      // already replaced are a different join's business.
      if (!state.meeting || payload?.meetingId !== state.meeting.id) return;
      const removedMeeting = state.meeting;
      const wasEjected = !removedMeeting[LEAVE_ISSUED];
      removedMeeting[LEAVE_ISSUED] = true;
      handleEjectedFromMeeting(removedMeeting, String(payload?.reason ?? ''), wasEjected);
    });
  }
};

// Cross-tab event listeners
if (typeof window !== 'undefined') {
  window.addEventListener('message', (event) => {
    if (!ALLOWED_ORIGINS.includes(event.origin)) {
      logger.warn('[WebEx] Rejected postMessage from untrusted origin', {
        origin: event.origin,
        allowed: ALLOWED_ORIGINS,
      });
      return;
    }

    const state = useWebexStore.getState();

    if (event.data?.type === 'webex-meeting-expired') {
      const { roomId } = event.data.data;
      logger.info('[WebEx] Received meeting expired event', { roomId, origin: event.origin });

      if (state.room === roomId && state.isInMeeting) {
        logger.info('[WebEx] Current meeting expired by host, leaving meeting');
        state.leaveMeetingExpired().catch((error) => {
          logger.error('[WebEx] Error leaving meeting after expiration:', error);
        });
      }
    }

    if (event.data?.type === 'webex-meeting-restart') {
      const { roomId, cycleId, reason } = event.data.data;
      logger.info('[WebEx] 📥 Received meeting restart event', {
        roomId,
        cycleId,
        reason,
        origin: event.origin,
      });
      console.log(
        '[WebEx] 📥 RECEIVE RESTART - Room ID:',
        roomId,
        'cycleId:',
        cycleId,
        'reason:',
        reason,
        'Current room:',
        state.room,
        'isInMeeting:',
        state.isInMeeting,
        'isMeetingHungUp:',
        state.isMeetingHungUp
      );

      if (state.room === roomId) {
        // One emit arrives three ways, and a delayed delivery of an older cycle must not
        // rejoin a healthy session — the set + initiator-timestamp check cover both.
        if (cycleId && !shouldProcessRestartCycle(roomId, cycleId, event.data.data.timestamp)) {
          console.log('[WebEx] ⏭️ Duplicate or stale restart cycle, skipping', cycleId);
          return;
        }

        // 'reboot' (the stop-recording cycle) force-rejoins even a client whose ejection
        // event never arrived — a stale isInMeeting is expected there (see below). Manual
        // restarts keep the original semantics: a host clicking Reconnect must not eject
        // participants who are genuinely still in the meeting.
        if (reason !== 'reboot' && state.isInMeeting && !state.isMeetingHungUp) {
          console.log('[WebEx] ⚠️ Manual restart while still in meeting — not rejoining');
          return;
        }

        logger.info('[WebEx] ✅ Restarting meeting as requested by host');
        console.log('[WebEx] ✅ Restarting meeting now...');
        // The green light arrived, so the optimistic window has done its job.
        // isInMeeting is deliberately NOT gated for reboots: a client whose ejection event
        // never arrived (the SDK delivers meeting:self:left only on the media-inactive
        // path — removed participants get meeting:removed on the meetings collection
        // instead) still reports true here, but the meeting is gone. reInitializeWebex
        // leaves any SDK-held meeting as its first step, so rejoining from a stale state
        // is safe.
        // Record the reason so the overlay can switch to the recording-reboot popup.
        useWebexStore.setState({ restartReason: reason ?? null });
        clearRebootWait();
        state.reInitializeWebex().catch((error) => {
          logger.error('[WebEx] ❌ Error restarting meeting:', error);
          useWebexStore.setState({ isRestarting: false, joinStage: '', restartReason: null });
        });
      } else {
        console.log('[WebEx] ⚠️ Not restarting - different room');
      }
    }
  });

  // Leave on abrupt close so others see the tile removed immediately.
  const handlePageUnload = () => {
    const state = useWebexStore.getState();
    if (state.meeting && state.isInMeeting) {
      try {
        state.meeting.leave();
      } catch (_e) {
        // Best-effort — browser may not honour async work during unload
      }
    }
  };
  window.addEventListener('beforeunload', handlePageUnload);
  window.addEventListener('pagehide', handlePageUnload);

  try {
    const channel = new BroadcastChannel('webex-meeting-channel');
    useWebexStore.setState({ broadcastChannel: channel });

    channel.addEventListener('message', (event) => {
      const state = useWebexStore.getState();

      if (event.data?.type === 'meeting-expired') {
        const { roomId } = event.data;
        logger.info('[WebEx] Received meeting expired event via BroadcastChannel', { roomId });

        if (state.room === roomId && state.isInMeeting) {
          logger.info('[WebEx] Current meeting expired by host, leaving meeting');
          state.leaveMeetingExpired().catch((error) => {
            logger.error('[WebEx] Error leaving meeting after expiration:', error);
          });
        }
      }

      if (event.data?.type === 'meeting-restart') {
        const { roomId, cycleId, reason, timestamp } = event.data;
        logger.info('[WebEx] Received meeting restart event via BroadcastChannel', {
          roomId,
          cycleId,
          reason,
        });

        if (state.room === roomId) {
          // Same dedupe + staleness check as the postMessage branch — see there.
          if (cycleId && !shouldProcessRestartCycle(roomId, cycleId, timestamp)) {
            console.log('[WebEx] ⏭️ Duplicate or stale restart cycle, skipping', cycleId);
            return;
          }

          // Same manual-vs-reboot gate as the postMessage branch — see there.
          if (reason !== 'reboot' && state.isInMeeting && !state.isMeetingHungUp) {
            console.log('[WebEx] ⚠️ Manual restart while still in meeting — not rejoining');
            return;
          }

          logger.info('[WebEx] Restarting meeting as requested by host');
          // Same as the postMessage branch: no isInMeeting gate — the restart event is the
          // authoritative signal, and reInitializeWebex handles a still-held meeting itself.
          useWebexStore.setState({ restartReason: reason ?? null });
          clearRebootWait();
          state.reInitializeWebex().catch((error) => {
            logger.error('[WebEx] Error restarting meeting:', error);
            useWebexStore.setState({ isRestarting: false, joinStage: '', restartReason: null });
          });
        }
      }
    });
  } catch (error) {
    logger.warn('[WebEx] BroadcastChannel not supported', error);
  }
}

// === Live captions helpers (Webex-native events, mirrors MeetingService) ===

const webexRoomPid = () => `webex:${useWebexStore.getState().room}`;

/** Caption events only process while live in the Webex meeting on its floor. */
const isInLiveWebexMeeting = (): boolean => {
  const state = useWebexStore.getState();
  if (!state.meeting || !state.isInMeeting) return false;

  const { currentFloorPresence } = useMultiFloorStateStore.getState();
  return !state.webexFloorPid || currentFloorPresence === state.webexFloorPid;
};

/** Translate a final transcript if the user has translation enabled (mirrors MeetingService.handleTranslation). */
const translateWebexTranscriptIfEnabled = (entry: TranscriptEntry) => {
  const {
    translationEnabled,
    targetLanguage,
    setTranscriptTranslating,
    updateTranscriptTranslation,
  } = useLiveCaptionsStore.getState();

  if (!translationEnabled || targetLanguage === 'original') return;

  setTranscriptTranslating(entry.id, true);
  translationService
    .translateText(entry.text, targetLanguage)
    .then((translatedText) => updateTranscriptTranslation(entry.id, translatedText))
    .catch((error) => {
      logger.error('[WebEx] Translation failed:', error);
      setTranscriptTranslating(entry.id, false);
    });
};

/** Broadcast a final transcript to the room (mirrors MeetingEventRouter.emitStreamingTranscriptionEvent). */
const broadcastWebexTranscript = (entry: TranscriptEntry) => {
  const { user, org } = useWorkplaceStore.getState();

  GlobalEventService.emitSafe(
    EventTypes.MeetingTranscriptionStream,
    {
      event_id: entry.id,
      user_pid: user?.pid || '',
      room_pid: webexRoomPid(),
      floor_pid: useWorkplaceStore.getState().currentRoom?.floor_pid || '',
      org_pid: org?.pid || '',
      transcript_data: {
        id: entry.id,
        text: entry.text,
        confidence: entry.confidence,
        isFinal: entry.isFinal,
        speaker: entry.speaker,
        timestamp: entry.timestamp,
      },
    },
    true
  );
};

/** Caption payload { captions, interimCaptions } is cumulative — dedupe finals by id; isFinal:false updates the live line only. */
const webexHandledCaptionIds = new Set<string>();

// Interim text is transient — coalesce to one store write per frame (latest wins).
let _pendingInterimText: string | null = null;
let _interimRaf: number | null = null;

const cancelPendingInterimUpdate = () => {
  if (_interimRaf !== null) cancelAnimationFrame(_interimRaf);
  _interimRaf = null;
  _pendingInterimText = null;
};

const scheduleInterimCaptionUpdate = (text: string) => {
  _pendingInterimText = text;
  if (_interimRaf !== null) return;

  _interimRaf = requestAnimationFrame(() => {
    _interimRaf = null;
    const latest = _pendingInterimText;
    _pendingInterimText = null;
    if (!latest) return;
    useLiveCaptionsStore.getState().updateCurrentTranscript(latest);
  });
};

const handleWebexNativeCaption = (payload: any) => {
  const state = useWebexStore.getState();
  if (!isInLiveWebexMeeting() || !state.isLiveCaptionsActive) return;

  const { addTranscript } = useLiveCaptionsStore.getState();

  const speakerName = (entry: any) => entry?.speaker?.name || entry?.speaker || 'Unknown Speaker';
  const showInterim = (text: string) => scheduleInterimCaptionUpdate(text);

  // Interim entries: show the latest live text for the bottom bar.
  const interim = payload?.interimCaptions ?? {};
  let latestInterim = '';
  for (const entries of Object.values(interim) as any) {
    for (const entry of entries ?? []) {
      if (entry?.text) latestInterim = entry.text;
    }
  }
  if (latestInterim) showInterim(latestInterim);

  // captions mixes finals with interim segments — store only new finals.
  const captions = payload?.captions ?? [];
  for (const entry of captions) {
    if (!entry?.text) continue;

    if (entry.isFinal === false) {
      showInterim(entry.text);
      continue;
    }

    const id = entry.id ? `webex-caption-${entry.id}` : nanoid();
    if (webexHandledCaptionIds.has(id)) continue;
    webexHandledCaptionIds.add(id);

    // Drop stale scheduled interim so a final's clear isn't overwritten.
    cancelPendingInterimUpdate();

    const transcriptEntry: TranscriptEntry = {
      id,
      text: entry.text,
      confidence: 1,
      isFinal: true,
      speaker: speakerName(entry),
      timestamp: Date.now(),
      roomPid: webexRoomPid(),
    };

    addTranscript(transcriptEntry);
    translateWebexTranscriptIfEnabled(transcriptEntry);
    broadcastWebexTranscript(transcriptEntry);
  }
};

/** Broadcast the live captions toggle so other participants start/stop their own transcription. */
const emitWebexLiveCaptionsToggle = (enabled: boolean) => {
  const { user, org } = useWorkplaceStore.getState();

  GlobalEventService.emitSafe(
    EventTypes.MeetingLivecaptionsToggle,
    {
      type: EventTypes.MeetingLivecaptionsToggle,
      user_pid: user?.pid,
      room_pid: webexRoomPid(),
      floor_pid: useWorkplaceStore.getState().currentRoom?.floor_pid || '',
      org_pid: org?.pid,
      enabled,
      timestamp: new Date().toISOString(),
      recording_pid: null,
      recording_info: null,
    },
    true
  );
};

/** Remote participant toggled captions for this room — start/stop without re-broadcasting (avoids loops). */
const handleRemoteLiveCaptionsToggle = (event: any) => {
  const { user } = useWorkplaceStore.getState();

  if (!isInLiveWebexMeeting()) return;
  if (event?.room_pid !== webexRoomPid()) return;
  if (!user || event?.user_pid === user.pid) return;

  logger.debug(
    `[WebEx] 📢 Remote captions ${event?.enabled ? 'started' : 'stopped'} by user ${event?.user_pid}`
  );

  if (event?.enabled) {
    useWebexStore
      .getState()
      .startLiveCaptions(false)
      .catch((error) => logger.warn('Failed to start captions', error));
  } else {
    useWebexStore
      .getState()
      .stopLiveCaptions(false)
      .catch((error) => logger.warn('Failed to stop captions', error));
  }
};

/** Remote participant's transcript for this room — show it locally. */
const handleRemoteTranscriptionStream = (event: any) => {
  const { user } = useWorkplaceStore.getState();

  if (!isInLiveWebexMeeting()) return;
  if (event?.room_pid !== webexRoomPid()) return;
  if (!user || event?.user_pid === user.pid) return;

  const data = event?.transcript_data;
  if (!data?.text) return;

  const entry: TranscriptEntry = {
    id: data.id,
    text: data.text,
    confidence: data.confidence,
    isFinal: data.isFinal,
    speaker: data.speaker || 'Remote Speaker',
    timestamp: data.timestamp,
    roomPid: event.room_pid,
  };

  useLiveCaptionsStore.getState().addTranscript(entry);
  if (data.isFinal) {
    translateWebexTranscriptIfEnabled(entry);
  }
};

// LLM socket state — voicea gates turnOnCaptions on llm.isConnected(); the connected event is a fast path.
let _webexLlmConnected = false;
let _webexLlmWaiters: Array<() => void> = [];

const notifyWebexLlmConnected = () => {
  _webexLlmConnected = true;
  _webexLlmWaiters.forEach((resolve) => resolve());
  _webexLlmWaiters = [];
};

const isWebexLlmConnected = (meeting: any): boolean =>
  _webexLlmConnected || !!meeting?.webex?.internal?.llm?.isConnected?.();

/** Wait for the LLM socket (polls llm.isConnected(), also resolves on the connected event). */
const waitForWebexLlmConnected = (meeting: any, timeoutMs = 10000): Promise<boolean> => {
  if (isWebexLlmConnected(meeting)) return Promise.resolve(true);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (connected: boolean) => {
      if (settled) return;
      settled = true;
      clearInterval(interval);
      clearTimeout(timer);
      _webexLlmWaiters = _webexLlmWaiters.filter((w) => w !== onConnected);
      resolve(connected);
    };
    const onConnected = () => finish(true);

    const timer = setTimeout(() => {
      const llm = meeting?.webex?.internal?.llm;
      logger.warn('[WebEx] ❌ LLM transcription socket not connected — captions unavailable', {
        llmPluginPresent: !!llm,
        llmConnected: !!llm?.isConnected?.(),
        // SDK may also log "Transcription Socket Connection Failed" (no LLM datachannel).
      });
      finish(false);
    }, timeoutMs);

    const interval = setInterval(() => {
      if (isWebexLlmConnected(meeting)) onConnected();
    }, 500);

    _webexLlmWaiters.push(onConnected);
  });
};

// Caption socket subscriptions live only while in a Webex meeting; keyed by
// `webex:${room}` so Jitsi's handlers (different room keys) stay inert.
let _webexLiveCaptionsSubscribed = false;
const subscribeWebexLiveCaptions = () => {
  if (_webexLiveCaptionsSubscribed) return;
  _webexLiveCaptionsSubscribed = true;

  GlobalEventService.on(EventTypes.MeetingLivecaptionsToggleTopic, handleRemoteLiveCaptionsToggle);
  GlobalEventService.on(
    EventTypes.MeetingTranscriptionStreamTopic,
    handleRemoteTranscriptionStream
  );
  logger.info('[WebEx] Live captions socket subscriptions registered');
};

const unsubscribeWebexLiveCaptions = () => {
  if (!_webexLiveCaptionsSubscribed) return;
  _webexLiveCaptionsSubscribed = false;

  GlobalEventService.off(EventTypes.MeetingLivecaptionsToggleTopic, handleRemoteLiveCaptionsToggle);
  GlobalEventService.off(
    EventTypes.MeetingTranscriptionStreamTopic,
    handleRemoteTranscriptionStream
  );
  // Reset per-meeting state (LLM connection, handled caption ids).
  _webexLlmConnected = false;
  _webexLlmWaiters = [];
  webexHandledCaptionIds.clear();
  cancelPendingInterimUpdate();
  logger.info('[WebEx] Live captions socket subscriptions removed');
};

export default useWebexStore;
